// Barcelona, "Sea breeze by the Sagrada". A warm night at the end of August in the Eixample. We stand on the north-east
// side of Plaça de Gaudí, on the pavement of Carrer de Lepant, and look south-west across the plaza and its pond at the
// Nativity facade of the Sagrada Familia, which faces the plaza across Carrer de la Marina.
// What is really there, left to right: the chamfered corner block of Mallorca and Lepant (our left hand, its wrought-
// iron balconies, green shutters and a senyera on a rail), a plane tree on the plaza kerb, the Eixample blocks across
// Carrer de Mallorca, the tower cranes over the unfinished Glory end of the basilica, the nave with its fruit-bowl
// pinnacles, the Nativity facade (its four perforated bell towers with their trencadis finials, the green cypress of
// the Tree of Life over the central portal), behind it the four towers of the Evangelists, the Tower of Jesus Christ
// with its lit cross and, over the apse on the right, the Tower of the Virgin Mary with its star; then the block across
// Carrer de Provenca with a scaffold and its advertising canvas (the city's billboard), another plane tree, and the
// corner block of Provenca and Lepant with a bar and its terrace under string lights. The plaza's palms and trees stand
// round the pond; the basilica is drawn about a third larger than the rest, but nothing is moved out of its order.
// Back to front: warm night sky, a waxing moon, thin cloud drifting in off the sea, a plane for El Prat -> far leaves
// -> the Eixample blocks, the scaffold and its canvas -> the cranes (the big one slews its jib) -> the basilica,
// floodlit and breathing, swifts circling its spires -> Carrer de la Marina's traffic, the plaza's palms and trees ->
// the pond (the basilica mirrored, ducks, rings) -> mid leaves -> the plaza's sandy walk and its people, the bike dock,
// the terrace -> the bike lane and Carrer de Lepant (a taxi, a moped) -> the corner blocks, the plane trees' canopies,
// the string lights, moths at the lamps -> near leaves.
// render(t, { crowd }) thins the people (see CROWD); crowd 0 draws nobody at all.
//
// The loop's timeline (seconds; every period divides 240):
//   event                                           time                       sound
//   taxi along Lepant, green "free" lamp (R to L)   4 - 18
//   a cyclist on the bike lane (L to R)             14 - 30
//   gulls cross over the towers                     26 - 44                    gull at 30
//   the guitarist ends a song, his ring claps       every 40 s (36, 76 ...)    3 s; a child runs a coin to his case
//   the waiter takes a tray out to a table          every 30 s (2, 32 ...)     out, serve, back: 18 s
//   someone walks a dog along the plaza (R to L)    40 - 72                    the cat on the pond's rim moves off
//   a moped on Lepant (L to R)                      52 - 60
//   a plane crosses towards El Prat                 64 - 104
//   a neighbour waters her geraniums                78 - 108                   drips from the balcony 84 - 100
//   a fish rises in the pond: rings                 96, 172                    splash
//   taxi, red lamp: taken (L to R)                  100 - 114
//   a rider takes a bike from the dock              104 - 140                  the dock beeps at 118
//   the basilica's bells: a phrase, three strokes   122; 124, 127.5, 131       chime, bell; the swifts circle wider
//   a couple strolls arm in arm (L to R)            136 - 168
//   a moped on Lepant (R to L)                      146 - 154
//   gulls again                                     164 - 182                  gull at 168
//   taxi, green lamp (R to L)                       180 - 194
//   the dog walker comes back (L to R)              186 - 218                  the cat moves back
//   a rider brings a bike back to the dock          196 - 230                  the dock beeps at 214
//   the neighbour comes out again                   196 - 214
//   a moped (L to R)                                206 - 214
//   a jogger along the kerb (R to L)                222 - 236
//   (and through the loop: a tourist filming as he walks, 6 - 38 and 148 - 180; two friends chatting, 78 - 112 and 200 - 234)
// The big jib swings once a loop; the floodlights breathe every 60 s; Mary's star pulses; windows switch on and off.
// Longest gap between two starts: 14 s (78 -> 92, 222 -> 236).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, scroll,
  hash, rng, bayer, hex, makeCanvas, glyph, FONT3, makeHalo, drawHalo, streak, reflect, text, textWidth, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const BASE = 160;                                   // the basilica's foot on Carrer de la Marina (hidden by the plaza)
const MARINA = 164;                                 // the far street's lamps and traffic
const P0 = 171, P1 = 200;                           // the pond's water rows [P0, P1)
const PX0 = 28, PX1 = 456;                          // the pond's ends (behind the corner blocks)
const WALK0 = 204, KERB = 233;                      // the plaza's sandy walk, its front kerb
const LANE = 243;                                   // the bike lane: wheels' row
const ROAD = 261;                                   // Carrer de Lepant: wheels' row
const BILL = { x: 330, y: 110, w: 48, h: 20 };      // the advertising canvas on the scaffold (the city's billboard)
const MOON = { x: 356, y: 30 };
const JX = 240;                                     // the Tower of Jesus Christ (focusX)
const CROSS_TOP = 10;                               // the top of its cross
const MARY = { x: 301, tip: 42 };                   // the Tower of the Virgin Mary; its star sits on the tip
const CRANE1 = { x: 172, top: 22 }, CRANE2 = { x: 140, top: 50 };
const LAMPS = [{ x: 104, y: 152 }, { x: 336, y: 152 }];      // the plaza's lamp posts (lantern centres), feet at WALK0 + 8
const LBLD = 46, RBLD = 446;                        // the corner blocks: the left one ends, the right one begins
const TREE_L = { x: 72, y: KERB }, TREE_R = { x: 392, y: KERB };
const BENCH = { x: 186, y: 216 };                   // left end, feet row
const DOCK = { x: 284, y: KERB - 1, n: 5, gap: 9 }; // the bike-share dock on the kerb, its bikes parked at an angle
const TABLES = [{ x: 372, y: 222 }, { x: 404, y: 229 }, { x: 428, y: 216 }];
const DOOR = { x: 458, y: 232 };                    // the bar's door
const BALC = { x: 6, y: 128 };                      // the neighbour's balcony (left end, floor row)

// The share of the people shown by default: 1 = everyone, 0.5 = about half. render(t, { crowd }) overrides it for a frame.
// Rank CAST (the guitarist, the waiter and the two tables he serves, the couple on the bench, the neighbour, whoever a
// timed event is about) stays at every level above 0; crowd 0 is nobody: an empty bench, the terrace laid but empty, the
// bikes all docked, no guitar. The cat, the ducks, the taxis and mopeds stay.
const CROWD = 0.6;
const CAST = -1;
const spread = i => frac((i + 0.5) * 0.6180339887);
const seen = (rank, crowd) => crowd > 0 && (rank === CAST || rank < crowd);

/* ================= Timeline (loop seconds) ================= */
const TAXIS = [{ t0: 4, t1: 18, dir: -1, free: true }, { t0: 100, t1: 114, dir: 1, free: false }, { t0: 180, t1: 194, dir: -1, free: true }];
const MOPEDS = [{ t0: 52, t1: 60, dir: 1 }, { t0: 146, t1: 154, dir: -1 }, { t0: 206, t1: 214, dir: 1 }];
const RIDES = [{ t0: 14, t1: 30, dir: 1, who: 'cycA' }, { t0: 160, t1: 176, dir: -1, who: 'cycB' }];
const DOGS = [{ t0: 40, t1: 72, dir: -1 }, { t0: 186, t1: 218, dir: 1 }];
const STROLL = { t0: 136, t1: 168 };
const JOG = { t0: 222, t1: 236 };
const GULL_T = [[26, 44, 1], [164, 182, -1]];
const PLANE = { t0: 64, t1: 104 };
const WATERING = [[78, 108], [196, 214]];           // the neighbour on her balcony
const SPLASH = [96, 172];
const CHIME_T = 122, BELLS = [124, 127.5, 131];
const SONG = 40, SONG_END = 36;                     // a song ends 36 s into every 40, the ring claps for 3 s
const SERVE = 30;                                   // the waiter's round
const TAKE = { walk: [104, 116], beep: 118, wheel: [118, 124], ride: [124, 140] };   // a bike taken from slot TAKE_SLOT
const BRING = { ride: [196, 212], beep: 214, walk: [216, 230] };                     // and brought back
const TAKE_SLOT = 2;
const CAT_MOVES = [[52, 60, 1], [196, 204, -1]];    // the cat trots off when the dog comes by, and back
const CAT_X = [230, 262];

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
/** a pixel buffer to build static layers in: put/has by pixel, flush() writes it to its canvas */
function pix(w = W, h = H) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  const p = { c, x, w, h, d,
    put(i, j, col) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, k = C(col); d[o] = k[0]; d[o + 1] = k[1]; d[o + 2] = k[2]; d[o + 3] = 255;
    },
    has(i, j) { return i >= 0 && j >= 0 && i < w && j < h && d[(j * w + i) * 4 + 3] > 0; },
    clear(i, j) { if (i >= 0 && j >= 0 && i < w && j < h) d[(j * w + i) * 4 + 3] = 0; },
    rect(i, j, rw, rh, col) { for (let b = 0; b < rh; b++) for (let a = 0; a < rw; a++) p.put(i + a, j + b, col); },
    flush() { x.putImageData(img, 0, 0); return c; },
  };
  return p;
}
/** a colour of a ramp for v in 0..1, ordered-dithered between neighbours */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
const MIXED = {};
function mixHex(a, b, t) {
  const key = a + b + t;
  if (MIXED[key]) return MIXED[key];
  const A = C(a), B = C(b), h2 = v => Math.round(v).toString(16).padStart(2, '0');
  return (MIXED[key] = '#' + h2(lerp(A[0], B[0], t)) + h2(lerp(A[1], B[1], t)) + h2(lerp(A[2], B[2], t)));
}
/** crop a full-screen layer to its painted bounding box so each frame blends only the pixels it needs */
function trim(c) {
  const w = c.width, h = c.height, d = c.getContext('2d').getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) if (d[(y * w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  if (x1 < 0) return { c, x: 0, y: 0 };
  const [t, tx] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  tx.drawImage(c, x0, y0, t.width, t.height, 0, 0, t.width, t.height);
  return { c: t, x: x0, y: y0 };
}
const blit = (L, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(L.c, L.x, L.y); ctx.globalAlpha = 1; };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
function line(p, x0, y0, x1, y1, col) {              // 1 px line into a pixel buffer
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0)) || 1;
  for (let k = 0; k <= n; k++) p.put(Math.round(x0 + (x1 - x0) * k / n), Math.round(y0 + (y1 - y0) * k / n), col);
}
function lineC(x0, y0, x1, y1, col) {                 // 1 px line on the frame
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0)) || 1;
  ctx.fillStyle = col;
  for (let k = 0; k <= n; k++) ctx.fillRect(Math.round(x0 + (x1 - x0) * k / n), Math.round(y0 + (y1 - y0) * k / n), 1, 1);
}
function flipH(src) {
  const [c, x] = makeCanvas(src.width, src.height);
  x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0);
  return c;
}
function sprite(rows, pal, flip = false) {
  const w = rows[0].length, [c, x] = makeCanvas(w, rows.length);
  rows.forEach((row, j) => { for (let i = 0; i < w; i++) if (pal[row[i]]) { x.fillStyle = pal[row[i]]; x.fillRect(flip ? w - 1 - i : i, j, 1, 1); } });
  return c;
}
/** FONT3 text at twice the size (each pixel a 2x2 block) */
function bigText(p, s, x, y, col, low = col) {
  let cx = x;
  for (const ch of s) {
    const g = FONT3[ch];
    if (g) g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') { p.rect(cx + i * 2, y + j * 2, 2, 1, col); p.put(cx + i * 2, y + j * 2 + 1, j === 4 ? low : col); p.put(cx + i * 2 + 1, y + j * 2 + 1, j === 4 ? low : col); } });
    cx += 8;
  }
}
/** a window's on/off at loop time t: it switches n times a loop at its own offset, lit a share p of the time */
const lit = (w, t, salt = 0) => hash(w.id + salt, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p;

/* ================= Sky: a warm late-summer night, the floodlights and the city glowing into it ================= */
const SKY = ['#060820', '#090b27', '#0c0f2e', '#101334', '#15173b', '#1b1b42', '#221f48', '#2b234d', '#352851', '#402c54',
  '#4c3256', '#5a3856', '#694054', '#784852', '#87524f'].map(hex);
function renderSky() {
  const [c, x] = makeCanvas(W, H);
  const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < 190; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 170), 1.4) * 0.72;
    v += Math.max(0, 1 - Math.hypot((i - JX) / 150, (y - 120) / 110)) * 0.3;            // the floodlit basilica
    v += Math.max(0, 1 - Math.hypot((i - 60) / 170, (y - 170) / 80)) * 0.14;            // the city towards the sea
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 90, (y - MOON.y) / 60)) * 0.14;      // the moon
    const f = clamp(v, 0, 1) * (SKY.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = SKY[Math.min(k, SKY.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
function renderMoon() {                               // a waxing gibbous moon, warm in the city haze
  const r = 7, s = r * 2 + 1, [c, x] = makeCanvas(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.3) continue;
    if (Math.hypot(dx + 4.2, dy + 0.6) < r - 0.4) continue;                               // the dark limb
    let col = dx + dy < -2 ? '#fbf1d8' : '#efe0be';
    if (dd > r - 1.1) col = '#d2bc94';
    if (Math.hypot(dx - 2, dy + 2) < 1.3 || Math.hypot(dx - 1, dy - 3) < 1.1 || Math.hypot(dx + 1, dy + 1) < 0.9) col = '#d8c6a0';
    x.fillStyle = col; x.fillRect(i, y, 1, 1);
  }
  return c;
}
const STARS = [];
{ const r = rng(908);
  while (STARS.length < 54) {
    const x = Math.floor(r() * W), y = Math.floor(Math.pow(r(), 1.4) * 110);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 18) continue;
    STARS.push({ x, y, k: 20 + Math.floor(r() * 60), p: r(), b: (0.25 + r() * 0.6) * (1 - y / 160), col: r() < 0.75 ? '#e6e8ff' : '#ffe8c8' });
  }
}

/* ================= Thin cloud off the sea (tileable; bellies lit by the city) ================= */
const CLOUD_DEFS = [
  { seed: 311, y: 4, h: 44, banks: 4, k: 1, a: 0.55, pal: { top: '#2a2c56', body: '#1a1c40', core: '#16183a', bot: '#3c3256', bot2: '#2e2a4e' } },
  { seed: 337, y: 30, h: 48, banks: 5, k: 2, a: 0.6, pal: { top: '#3a3a66', body: '#262850', core: '#20224a', bot: '#64465e', bot2: '#4a3a5a' } },
  { seed: 353, y: 70, h: 40, banks: 4, k: 3, a: 0.5, pal: { top: '#4a4470', body: '#33305a', core: '#2c2a54', bot: '#8a5a62', bot2: '#6a4a60' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 50 + r() * 70, base = h * (0.5 + r() * 0.3);
    const np = 5 + Math.floor(r() * 5);
    for (let i = 0; i < np; i++) {
      const rx = 10 + r() * 18, ry = 3 + r() * 5;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.2, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.4;
      if (v > best) best = v;
    }
    best += 0.2 * Math.sin(TAU * 13 * x / W + y * 0.47 + def.seed) * Math.sin(TAU * 6 * x / W - y * 0.29);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const [c, cx] = makeCanvas(W, h);
  const img = cx.createImageData(W, h), d = img.data, P = {};
  for (const k in def.pal) P[k] = hex(def.pal[k]);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.34, v = D[y * W + x];
    if (v + th <= 0.05) continue;
    let col;
    if (get(x, y - 1) + th <= 0.05 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.05) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    const o = (y * W + x) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  cx.putImageData(img, 0, 0);
  return { c, y: def.y, k: def.k, a: def.a };
}
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) {
    const a = s.b * (0.6 + 0.4 * wave(t, s.k, s.p));
    if (a < 0.08) continue;
    ctx.globalAlpha = Math.min(1, a); R(s.x, s.y, 1, 1, s.col);
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.85 + 0.15 * wave(t, 4)); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - 7, MOON.y - 7);
  for (const cl of CLOUDS) {
    const off = scroll(t, cl.k, W);
    ctx.globalAlpha = cl.a; ctx.drawImage(cl.c, off - W, cl.y); ctx.drawImage(cl.c, off, cl.y);
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.35);                          // its glow through the cloud
  // a plane letting down towards El Prat over the sea, its strobe and beacons
  if (t >= PLANE.t0 && t < PLANE.t1) {
    const u = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(lerp(W + 6, -6, u)), y = Math.round(lerp(26, 58, u));
    const a = Math.min(1, u * 8, (1 - u) * 8);
    ctx.globalAlpha = a * 0.8; R(x - 2, y, 5, 1, '#3a3a5a'); R(x, y - 1, 1, 1, '#3a3a5a');
    ctx.globalAlpha = a; R(x - 2, y, 1, 1, frac(t * 1.25) < 0.5 ? '#ff5a4a' : '#7a2a2a'); R(x + 2, y, 1, 1, '#6aff8a');
    if (frac(t * 0.75) < 0.12) { R(x, y, 1, 1, '#ffffff'); halo(HL.strobe, x, y, a * 0.7); }
    ctx.globalAlpha = 1;
  }
}

/* ================= The Eixample: chamfered blocks, wrought-iron balconies, shutters, roof terraces ================= */
const FACADE = ['#17141f', '#1e1925', '#272029', '#30272e', '#3b2f33', '#483839'];
const SHUT_G = ['#1f3329', '#2c4636', '#3c5a44'];            // green persianes
const WIN_LIT = ['#f6c06a', '#ffd690', '#eaa856', '#ffe2a8'];
const WINS = [];                                              // windows that switch: { id, x, y, w, h, col, n, p, off }
const BLOCKS = [
  { x0: 40, x1: 152, top: 98, cham: [136, 152], seed: 11, dim: 0 },     // across Carrer de Mallorca
  { x0: 118, x1: 176, top: 122, cham: null, seed: 12, dim: 0.12 },      // behind it, beside the nave
  { x0: 296, x1: 332, top: 124, cham: null, seed: 13, dim: 0.12 },      // behind the apse
  { x0: 318, x1: 446, top: 100, cham: [318, 334], seed: 14, dim: 0 },   // across Carrer de Provenca (the scaffold)
];
function paintBlock(p, B, scaffold) {
  const r = rng(B.seed * 97);
  for (let x = B.x0; x < B.x1; x++) {
    const ch = B.cham && x >= B.cham[0] && x < B.cham[1];
    for (let y = B.top; y < BASE + 2; y++) {
      const d = y - B.top;
      let v = 0.18 + (y - B.top) / (BASE - B.top) * 0.55 - B.dim + (ch ? -0.12 : 0) + (hash(x, y, B.seed) - 0.5) * 0.1;
      if (d < 2) v = d === 0 ? 0.95 : 0.5;                                  // the cornice
      p.put(x, y, pick(FACADE, v, x, y));
    }
    if (hash(x, 1, B.seed) < 0.5 && (x & 1)) p.put(x, B.top - 1, '#3a3038');   // the parapet's balusters
    p.put(x, B.top - 2, '#4a3e40');
  }
  // floors of windows with a balcony each; the chamfer's windows are narrower
  const sx = (B.x0 & 1) ? 1 : 0;
  for (let fy = B.top + 4; fy < BASE - 4; fy += 8) for (let x = B.x0 + 3 + sx; x < B.x1 - 3; x += 7) {
    const ch = B.cham && x >= B.cham[0] - 1 && x < B.cham[1], ww = ch ? 2 : 3;
    if (scaffold && x + ww > BILL.x - 4 && x < BILL.x + BILL.w + 4 && fy > BILL.y - 10 && fy < BILL.y + BILL.h + 4) continue;
    const v = r(), kind = v < 0.22 ? 'lit' : v < 0.34 ? 'half' : v < 0.62 ? 'shut' : 'dark';
    for (let j = 0; j < 5; j++) for (let i = 0; i < ww; i++) {
      let col = '#141220';
      if (kind === 'lit') col = j < 2 ? WIN_LIT[(x + fy) % 4] : WIN_LIT[(x + fy + 1) % 4];
      else if (kind === 'half') col = j < 3 ? SHUT_G[j & 1 ? 1 : 0] : WIN_LIT[0];
      else if (kind === 'shut') col = (j & 1) ? SHUT_G[0] : SHUT_G[B.dim ? 0 : 1];
      p.put(x + i, fy + j, col);
    }
    if (!ch && fy > B.top + 4) {                                            // the wrought-iron balcony rail
      for (let i = -1; i <= ww; i++) { p.put(x + i, fy + 5, (i + fy) & 1 ? '#0e0c16' : '#2a2430'); p.put(x + i, fy + 6, '#0e0c16'); }
      p.put(x - 1, fy + 4, '#0e0c16'); p.put(x + ww, fy + 4, '#0e0c16');
    }
    if (kind === 'dark' && r() < 0.5) WINS.push({ id: WINS.length, x, y: fy, w: ww, h: 5, col: WIN_LIT[(x >> 1) % 4], n: 4 + Math.floor(r() * 6), p: 0.45, off: r() });
    else if (kind === 'lit' && r() < 0.25) WINS.push({ id: WINS.length, x, y: fy, w: ww, h: 5, col: '#7896d8', n: 6 + Math.floor(r() * 6), p: 0.5, off: r(), tv: true });
  }
  // the roof terrace: a stair house, water tanks, aerials
  for (let k = 0; k < 3; k++) {
    const bx = Math.round(lerp(B.x0 + 8, B.x1 - 14, r())), kind = Math.floor(r() * 3), y0 = B.top - 2;
    if (kind === 0) { for (let j = 1; j <= 6; j++) for (let i = 0; i < 9; i++) p.put(bx + i, y0 - j, j === 6 ? '#3e3438' : i === 7 && j < 4 ? '#e8a858' : pick(FACADE, 0.3, bx + i, y0 - j)); }
    else if (kind === 1) { for (let j = 1; j <= 3; j++) for (let i = 0; i < 4; i++) p.put(bx + i, y0 - j, j === 3 ? '#4a4048' : '#2a2430'); p.put(bx + 1, y0 - 4, '#2a2430'); }
    else { for (let j = 1; j <= 9; j++) p.put(bx, y0 - j, '#2a2632'); for (let i = -2; i <= 2; i++) p.put(bx + i, y0 - 7, '#2a2632'); p.put(bx - 1, y0 - 4, '#2a2632'); p.put(bx + 1, y0 - 4, '#2a2632'); }
  }
}
function renderFar(scaffold) {
  const p = pix();
  // the low roofs of the city far behind the basilica
  for (let x = 120; x < 340; x++) {
    const top = 138 + Math.floor(hash(x >> 3, 1, 5) * 8);
    for (let y = top; y < BASE + 2; y++) p.put(x, y, y === top ? '#2c2632' : (x % 5 === 2 && (y - top) % 4 === 2 && hash(x, y, 6) < 0.3) ? '#d8a058' : '#1a1622');
  }
  for (const B of BLOCKS) paintBlock(p, B, scaffold && B.seed === 14);
  return p.flush();
}

/* ================= The scaffold and its canvas (the city's billboard): an ad for Cava Estel ================= */
const BILL_LAMPS = [BILL.x + 8, BILL.x + 24, BILL.x + 40], BILL_LAMP_Y = BILL.y - 5;
function renderScaffold() {
  const p = pix(), L = BILL.x - 4, Rr = BILL.x + BILL.w + 3, T = BILL.y - 8, B = BILL.y + BILL.h + 12;
  for (let x = L; x <= Rr; x += 7) for (let y = T; y <= B; y++) p.put(x, y, (y & 3) === 0 ? '#4a4450' : '#2e2a36');   // standards
  for (let y = T + 2; y <= B; y += 6) for (let x = L - 1; x <= Rr + 1; x++) p.put(x, y, (x & 1) ? '#3e3844' : '#5a525c');   // ledgers, boards
  for (let x = L; x + 7 <= Rr; x += 7) line(p, x, B, x + 7, B - 6, '#26222e');                                         // a brace
  for (let y = T + 3; y <= B; y += 6) for (let x = L; x <= Rr; x++) if (hash(x, y, 81) < 0.5) p.put(x, y + 1, '#1a1822');   // netting
  for (let y = BILL.y - 1; y <= BILL.y + BILL.h; y++) for (let x = BILL.x - 1; x <= BILL.x + BILL.w; x++) p.put(x, y, '#0e0c16');   // the canvas' frame
  for (const lx of BILL_LAMPS) {                                            // little floodlights on arms over the canvas
    for (let y = BILL_LAMP_Y; y < BILL.y - 1; y++) p.put(lx, y, '#26222e');
    p.rect(lx - 2, BILL_LAMP_Y - 1, 4, 2, '#2a2632'); p.rect(lx - 2, BILL_LAMP_Y + 1, 4, 1, '#fff2c8');
  }
  return trim(p.flush());
}
const AD_BUBBLES = [[7, 0, 9], [8, 0.37, 11], [9, 0.71, 8], [8, 0.2, 13], [10, 0.55, 10]];   // column, phase, cycles a loop / 4
function renderAd() {
  const { w, h } = BILL, p = pix(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const v = 0.15 + y * 0.03 + Math.max(0, 1 - Math.hypot((x - 8) / 12, (y - 10) / 10)) * 0.35;
    p.put(x, y, pick(['#120e2a', '#1c1438', '#2a1a44', '#3c2250', '#56305a'], v, x, y));
  }
  for (let x = 0; x < w; x++) { p.put(x, 0, '#f2b134'); p.put(x, h - 1, '#a86a1c'); }
  for (let y = 0; y < h; y++) { p.put(0, y, '#d08a24'); p.put(w - 1, y, '#a86a1c'); }
  // a coupe of cava: bowl, stem, foot
  const bowl = [[3, 13], [3, 13], [4, 12], [5, 11], [6, 10]];
  bowl.forEach(([a, b], j) => { for (let x = a; x <= b; x++) p.put(x, 5 + j, j === 0 ? '#fff4d0' : x === a || x === b ? '#e8d8a8' : j === 1 ? '#fff0b0' : '#f6d264'); });
  for (let y = 10; y < 15; y++) p.put(8, y, '#e8e0d0');
  for (let x = 5; x <= 11; x++) p.put(x, 15, x === 5 || x === 11 ? '#a8a0b0' : '#e8e0d0');
  bigText(p, 'CAVA', 17, 2, '#fff1d0', '#f2b134');                                                          // a gold lowlight on the letters
  const c = p.flush();
  text(p.x, 'ESTEL', 21, 13, '#f2b134');
  return c;
}
function drawBillboard(t, pitch) {
  halo(HL.bill, BILL.x + (BILL.w >> 1), BILL.y + 6, 0.9 + 0.1 * wave(t, 8, 0.2));
  blit(SCAF_C);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(AD_C, BILL.x, BILL.y);
    for (const [bx, ph, k] of AD_BUBBLES) {                                  // bubbles rise in the coupe
      const u = frac(k * 4 * t / LOOP + ph), y = Math.round(9 - u * 4);
      R(BILL.x + bx, BILL.y + y, 1, 1, u > 0.8 ? '#fffbe8' : '#fff0b0');
    }
    const tw = 0.5 + 0.5 * wave(t, 30);                                       // the star after ESTEL twinkles
    const sx = BILL.x + 43, sy = BILL.y + 15;
    R(sx, sy, 1, 1, '#fff8e0'); ctx.globalAlpha = 0.4 + 0.6 * tw;
    R(sx - 1, sy, 1, 1, '#f2b134'); R(sx + 1, sy, 1, 1, '#f2b134'); R(sx, sy - 1, 1, 1, '#f2b134'); R(sx, sy + 1, 1, 1, '#f2b134');
    ctx.globalAlpha = 1;
    const sweep = frac(t / 24) * 90 - 20;                                    // a slow sheen across the canvas
    if (sweep > 0 && sweep < BILL.w) { ctx.globalAlpha = 0.14; R(BILL.x + Math.round(sweep), BILL.y + 1, 2, BILL.h - 2, '#fff4d8'); ctx.globalAlpha = 1; }
  }
  PITCH.draw(ctx, t, BILL.x, BILL.y, pitch);
  BILL_LAMPS.forEach((lx, i) => halo(HL.billLamp, lx, BILL_LAMP_Y + 2, 0.9 + 0.1 * wave(t, 700 + i * 37)));
}

/* ================= The Sagrada Familia, floodlit =================
   Old stone of the Nativity (warm, darker) and the new stone of the central towers (pale, lit white). Every tower is a
   tapering shaft and a crown; the Nativity's four are perforated (rows of louvres in a honeycomb) under trencadis
   finials. Built once into a pixel buffer; per frame only the lights move (see drawBasilica). */
const NAT = ['#1a110d', '#2b1c15', '#412b1e', '#5b3e27', '#7a5532', '#9a6e40', '#b98a52', '#d4a768', '#eac486', '#fbe0ae'];
const NEW = ['#1e1c28', '#2e2a36', '#433c48', '#5c535c', '#786c70', '#958680', '#b0a092', '#cabaa6', '#e0d2bc', '#f4ead8'];
const CYPRESS = ['#122818', '#1b3a22', '#26502c', '#346a38', '#468648', '#62a45c', '#88c27a'];
const TREN = ['#f6efe0', '#f0b030', '#c83c2c', '#f0b030'];        // trencadis bands: white, gold, red, gold
const NATIVITY = [{ x: 203, tip: 74, hw: 6 }, { x: 220, tip: 60, hw: 7 }, { x: 260, tip: 60, hw: 7 }, { x: 277, tip: 74, hw: 6 }];
const EVANG = [{ x: 214, tip: 50, hw: 4.5, far: true, sym: 'eagle' }, { x: 266, tip: 50, hw: 4.5, far: true, sym: 'angel' },
  { x: 227, tip: 42, hw: 5, sym: 'lion' }, { x: 253, tip: 42, hw: 5, sym: 'bull' }];
// The symbols crowning the Evangelists, 7 x 5 px each and each its own silhouette: Matthew's winged man (raised wings),
// Mark's lion (a mane), Luke's bull (horns) and John's eagle (wings spread wide). They are pale stone with gilt touches,
// floodlit like the cross on the Tower of Jesus, so they read as part of the towers rather than as coloured badges.
const EVANG_SYM = {
  angel: ['W.gGg.W', 'WW.s.WW', '.WWWWW.', '..WWW..', '..WWW..'],
  lion: ['..GGG..', '.GGsGG.', 'GGsWsGG', '.GWWWG.', '..WWW..'],
  bull: ['W.....W', '.WsWsW.', '.WWWWW.', '..WsW..', '..WWW..'],
  eagle: ['...Wg..', 'W..W..W', 'WWWWWWW', '.WWWWW.', '..W.W..'],
};
const EVANG_PAL = { W: '#f4ecdc', s: '#d8c6aa', G: '#e8c878', g: '#f0d890' };
const NAVE_PIN = [154, 162, 170, 178, 186, 194];
const APSE_PIN = [{ x: 286, tip: 100 }, { x: 293, tip: 95 }, { x: 310, tip: 96 }, { x: 317, tip: 100 }, { x: 323, tip: 106 }];
const SPARKS = [];                                    // trencadis and fruit pixels that catch a glint now and then
function renderBasilica() {
  const p = pix(), r = rng(1882);
  /** a shaft from row y0 to y1 (hw0 at the top, hw1 at the foot), centred on cx */
  const shaft = (cx, y0, y1, hw0, hw1, ramp, o = {}) => {
    for (let y = y0; y < y1; y++) {
      const s = (y - y0) / Math.max(1, y1 - y0), hw = lerp(hw0, hw1, Math.pow(s, o.pow || 0.9));
      const xa = Math.round(cx - hw), xb = Math.round(cx + hw) - 1;
      for (let x = xa; x <= xb; x++) {
        const a = (x + 0.5 - cx) / Math.max(1, hw);
        let v = (o.v ?? 0.5) + 0.3 * s + 0.16 * a - 0.3 * a * a - (o.dim || 0) + (hash(x, y, 7) - 0.5) * 0.08;
        if (x === xa) v -= 0.18;
        if (o.ribs && ((x - Math.round(cx)) % 3 === 0) && Math.abs(a) < 0.85) v -= 0.12;
        if (o.rings && (y - y0) % o.rings === 0) v += 0.16;
        let col = pick(ramp, v, x, y);
        if (o.holes && Math.abs(a) < 0.78 && y % 3 === 0 && ((x + Math.floor(y / 3)) & 1) === 0) col = o.hole;
        if (o.windows && Math.abs(a) < 0.6 && (y - y0) % 6 === 3 && (x - Math.round(cx)) % 3 === 1) col = ramp[1];
        if (o.letters && s < 0.22 && s > 0.05 && Math.abs(a) < 0.7 && ((x * 3 + y) % 7 === 0)) col = '#f2c040';   // "Sanctus" in gold
        p.put(x, y, col);
      }
    }
  };
  /** a trencadis finial (bishop's mitre) from row tip down to the neck, its widest about 3 px each side */
  const mitre = (cx, tip, len, hwN) => {
    for (let y = tip; y < tip + len; y++) {
      const u = (y - tip) / len;
      let hw;
      if (u < 0.16) hw = 1;
      else { const w = (u - 0.16) / 0.84; hw = lerp(1.3, hwN, w) + 1.7 * Math.sin(Math.PI * w); }
      const xa = Math.round(cx - hw), xb = Math.round(cx + hw) - 1;
      for (let x = xa; x <= xb; x++) {
        let col;
        if (u < 0.16) col = (y - tip) < 1 ? '#fff6dc' : '#f6d060';
        else {
          const band = Math.floor((y - tip) / 2) % 4;
          col = TREN[band];
          const hv = hash(x, y, 31);
          if (hv < 0.14) col = '#fffaf0'; else if (hv < 0.2) col = '#3a8a5a'; else if (hv < 0.24) col = '#3a62b0';
          if (x === xa) col = mixHex(col, '#2a1a2a', 0.45); else if (x === xb && xb > xa) col = mixHex(col, '#fff6dc', 0.25);
          if (hv > 0.9) SPARKS.push({ x, y, id: SPARKS.length });
        }
        p.put(x, y, col);
      }
    }
    p.put(Math.round(cx - 0.5), tip - 1, '#ffffff');                       // the little cross on top
    p.put(Math.round(cx - 1.5), tip, '#fff6dc'); p.put(Math.round(cx + 0.5), tip, '#fff6dc');
  };

  // ---- the Tower of the Virgin Mary over the apse: a pale shaft, a crystal crown (its star is drawn per frame)
  shaft(MARY.x, MARY.tip + 8, BASE, 3, 8, NEW, { ribs: true, rings: 9, v: 0.5, dim: 0.04 });
  for (let y = MARY.tip; y < MARY.tip + 8; y++) for (let x = MARY.x - 3; x <= MARY.x + 2; x++)
    p.put(x, y, (x + y) % 3 === 0 ? '#9ab0d8' : y === MARY.tip ? '#f4f8ff' : '#d6e2f4');
  // ---- the four Evangelists: the far pair first, then the near pair in front of the Tower of Jesus
  const evangelist = E => {
    shaft(E.x, E.tip + 7, BASE, 2, E.hw * 1.6, NEW, { ribs: true, rings: 8, v: E.far ? 0.44 : 0.52, dim: E.far ? 0.06 : 0 });
    for (let y = E.tip + 3; y < E.tip + 7; y++) {                             // the crown narrowing up into the symbol
      const hw = y < E.tip + 5 ? 1 : 2;
      for (let x = E.x - hw; x <= E.x + hw; x++) p.put(x, y, x === E.x - hw ? '#b8a894' : '#e6d8c0');
    }
    EVANG_SYM[E.sym].forEach((row, j) => {                                    // its symbol, shaded on the side away from the floodlights
      const left = row.search(/[^.]/);
      for (let i = 0; i < row.length; i++) if (row[i] !== '.') {
        let col = EVANG_PAL[row[i]];
        if (i === left) col = mixHex(col, '#5c4c54', 0.35);
        p.put(E.x - 3 + i, E.tip - 2 + j, E.far ? mixHex(col, '#1e1c28', 0.14) : col);
      }
    });
  };
  EVANG.filter(E => E.far).forEach(evangelist);
  // ---- the Tower of Jesus Christ: ribbed shaft, a glazed lantern, the cross
  shaft(JX, CROSS_TOP + 24, BASE, 3, 11, NEW, { ribs: true, rings: 10, windows: true, v: 0.56, pow: 0.8 });
  for (let y = CROSS_TOP + 12; y < CROSS_TOP + 24; y++) {
    const hw = y < CROSS_TOP + 15 ? 2 : 3;
    for (let x = JX - hw; x < JX + hw; x++) p.put(x, y, (x - JX) % 2 === 0 ? '#a8b8d8' : y === CROSS_TOP + 15 ? '#f8fbff' : '#dce6f6');
  }
  for (let y = CROSS_TOP; y < CROSS_TOP + 12; y++) for (let x = JX - 1; x <= JX + 1; x++) p.put(x, y, x === JX ? '#ffffff' : '#dfe8fa');
  for (let x = JX - 5; x <= JX + 5; x++) for (let y = CROSS_TOP + 3; y <= CROSS_TOP + 5; y++) p.put(x, y, y === CROSS_TOP + 4 ? '#ffffff' : '#dfe8fa');
  EVANG.filter(E => !E.far).forEach(evangelist);
  // ---- the nave towards the Glory end: a wall, clerestory, pinnacles crowned with bowls of fruit
  for (let x = 146; x < 200; x++) for (let y = 116; y < BASE; y++) {
    let v = 0.3 + (y - 116) / 44 * 0.35 + (hash(x, y, 9) - 0.5) * 0.12;
    if ((x - 146) % 8 === 4 && y > 120 && y < 146) v = 0.05;                   // tall windows
    else if ((x - 146) % 8 === 0) v += 0.15;
    p.put(x, y, pick(NAT, v, x, y));
  }
  for (let x = 146; x < 200; x++) for (let y = 110; y < 116; y++) p.put(x, y, pick(NEW, 0.25 + (y - 110) * 0.04, x, y));   // the roof
  const FRUIT = ['#e8582c', '#f2a830', '#f6d84a', '#c83a5a', '#6ab04a', '#f08a3a'];
  for (const nx of NAVE_PIN) {
    const tip = 101 + (nx * 7) % 5;
    shaft(nx, tip + 3, 116, 1, 2.2, NAT, { v: 0.5 });
    for (const [i, j] of [[-1, 0], [0, 0], [1, 0], [-1, 1], [0, 1], [1, 1], [0, -1]]) {
      const col = FRUIT[Math.floor(hash(nx, i * 3 + j, 41) * FRUIT.length)];
      p.put(nx + i, tip + j, col); SPARKS.push({ x: nx + i, y: tip + j, id: SPARKS.length, fruit: true });
    }
  }
  // ---- the apse: a ring of steep pinnacles round the Tower of Mary's foot, stained glass lit from inside
  for (let x = 282; x < 326; x++) for (let y = 112; y < BASE; y++) {
    let v = 0.32 + (y - 112) / 48 * 0.36 + (hash(x, y, 10) - 0.5) * 0.12;
    if ((x - 282) % 9 === 4 && y > 118 && y < 150) v = -1;
    p.put(x, y, v < 0 ? (y < 124 ? '#3a2418' : hash(x, y >> 2, 14) < 0.3 ? '#4a5a8a' : '#8a5a30') : pick(NAT, v, x, y));
  }
  for (const A of APSE_PIN) { shaft(A.x, A.tip + 2, 116, 1, 3, NAT, { v: 0.45 }); p.put(A.x - 1, A.tip, '#f2c860'); p.put(A.x - 1, A.tip + 1, '#e8a840'); }
  // ---- the Nativity facade: organic, dripping stone between and under its towers
  const ftop = x => Math.round(116 + 1.6 * Math.sin(x * 0.7) + 1.6 * Math.sin(x * 0.31) + (x < 200 ? (200 - x) * 1.6 : 0) + (x > 280 ? (x - 280) * 1.6 : 0));
  for (let x = 194; x < 287; x++) for (let y = ftop(x); y < BASE; y++) {
    const drip = Math.sin(x * 1.7 + hash(x, 0, 3) * 6) * 0.5 + 0.5;
    let v = 0.42 + (y - 114) / 46 * 0.32 + drip * 0.12 * ((y + Math.floor(hash(x, 1, 3) * 6)) % 7 < 4 ? 1 : -1) + (hash(x, y, 11) - 0.5) * 0.16;
    if (hash(x >> 1, y >> 1, 12) < 0.1) v -= 0.3;                           // deep crevices among the figures
    if (y === ftop(x)) v = 0.78;
    p.put(x, y, pick(NAT, v, x, y));
  }
  // its four bell towers, perforated, under their finials
  for (const N of NATIVITY) {
    const neck = N.tip + Math.round((BASE - N.tip) * 0.2);
    shaft(N.x, neck, 132, 2.4, N.hw, NAT, { holes: true, hole: '#160d0a', letters: true, v: 0.46, pow: 1.1 });
    mitre(N.x, N.tip, neck - N.tip, 2.4);
  }
  // the portals: Hope and Faith either side, Charity in the middle under the gable
  const arch = (x0, x1, top, glow) => {
    const cx = (x0 + x1) / 2, hw = (x1 - x0) / 2;
    for (let y = top; y < BASE; y++) {
      const peak = Math.max(0, (top + hw * 1.2 - y) / (hw * 1.2)), w = hw * Math.sqrt(1 - peak * peak);
      for (let x = Math.round(cx - w); x < Math.round(cx + w); x++) {
        const edge = x === Math.round(cx - w) || x === Math.round(cx + w) - 1;
        const g = (y - top) / (BASE - top);
        p.put(x, y, edge ? '#eac486' : g > 0.55 && glow ? pick(['#5a3418', '#8a5424', '#c8843a', '#f0b25a'], 0.3 + (g - 0.55) * 1.6, x, y) : pick(NAT, 0.12 + g * 0.1, x, y));
      }
    }
  };
  arch(229, 252, 121, true); arch(206, 216, 132, false); arch(264, 274, 132, false);
  // the gable over Charity and the Tree of Life on it, white doves in its branches
  for (let y = 104; y < 122; y++) { const hw = (y - 104) * 0.8; for (let x = Math.round(240 - hw); x < Math.round(240 + hw); x++) p.put(x, y, pick(NAT, 0.55 + (y - 104) * 0.02 + (hash(x, y, 13) - 0.5) * 0.2, x, y)); }
  for (let y = 82; y < 108; y++) {
    const s = (y - 82) / 26, hw = s < 0.75 ? 0.6 + s * 4.4 : 3.9 - (s - 0.75) * 6;
    for (let x = Math.round(240 - hw); x < Math.round(240 + hw); x++) {
      const a = (x + 0.5 - 240) / Math.max(1, hw);
      p.put(x, y, pick(CYPRESS, 0.35 + s * 0.35 + 0.2 * a - 0.2 * a * a + ((x + y * 2) % 3 === 0 ? -0.15 : 0), x, y));
    }
  }
  p.put(239, 81, '#d8c060'); p.put(240, 80, '#fff4c8');
  for (const [dx, dy] of [[-3, 92], [2, 88], [-4, 101], [3, 97], [0, 95], [-2, 104]]) p.put(240 + dx, dy, '#f4f4f0');
  const c = p.flush();
  // the floodlights' extra light, drawn additively and breathing: warm on the old stone, cool on the new
  const [g, gx] = makeCanvas(W, H), gi = gx.createImageData(W, H), gd = gi.data, sd = p.d;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const o = (y * W + x) * 4;
    if (!sd[o + 3]) continue;
    const k = clamp((y - 20) / 140, 0, 1), cool = sd[o + 2] > sd[o];
    gd[o] = cool ? 150 : 255; gd[o + 1] = cool ? 170 : 170; gd[o + 2] = cool ? 220 : 90;
    gd[o + 3] = Math.round(255 * (0.25 + 0.75 * k) * (bayer(x, y) < 0.5 + 0.5 * k ? 1 : 0.5));
  }
  gx.putImageData(gi, 0, 0);
  return { c: trim(c), glow: trim(g) };
}

/* ================= The tower cranes over the Glory end ================= */
function renderMasts() {
  const p = pix();
  for (const K of [CRANE2, CRANE1]) {
    for (let y = K.top; y < BASE; y++) {
      const lo = clamp((y - 100) / 60, 0, 1), edge = mixHex('#2c2838', '#8a5a3a', lo * 0.6), web = mixHex('#1e1a28', '#5a3a2a', lo * 0.5);
      p.put(K.x - 1, y, edge); p.put(K.x + 1, y, edge);
      const m = (y - K.top) % 6;
      if (m === 0) p.put(K.x, y, edge); else if (m === 3) p.put(K.x, y, web);   // struts and the lattice between them
    }
    p.rect(K.x - 2, K.top - 1, 5, 2, '#3a3444');                                // the slewing ring and cab
    p.rect(K.x + 2, K.top + 1, 3, 3, '#2a2634'); p.put(K.x + 3, K.top + 2, '#e8c070');
  }
  // the fixed jib of the smaller crane, pointing away over the Glory end
  const K = CRANE2;
  for (let x = K.x - 38; x <= K.x + 12; x++) { p.put(x, K.top, '#3a3446'); if (x < K.x - 1 || x > K.x + 1) { p.put(x, K.top + 2, '#2c2838'); if ((x & 3) === 0) p.put(x, K.top + 1, '#2c2838'); } }
  p.rect(K.x + 9, K.top + 1, 4, 4, '#26222e');
  for (let k = 0; k <= 6; k++) p.put(K.x, K.top - 1 - k, '#3a3446');
  line(p, K.x, K.top - 7, K.x - 26, K.top, '#2a2636'); line(p, K.x, K.top - 7, K.x + 12, K.top, '#2a2636');
  for (let y = K.top + 3; y < K.top + 22; y++) p.put(K.x - 20, y, '#22202c');
  p.rect(K.x - 21, K.top + 22, 3, 2, '#c8a040');
  return p.flush();
}
const SLEW = t => 0.9 + 0.4 * wave(t, 1, 0.25);        // the big crane's jib angle (radians off the picture plane): once a loop
function drawCranes(t) {
  ctx.drawImage(MASTS_C, 0, 0);
  const K = CRANE1, th = SLEW(t), co = Math.cos(th), dy = Math.round(Math.sin(th) * 3);
  const jx = Math.round(K.x - 72 * co), cx = Math.round(K.x + 24 * co), y0 = K.top;
  lineC(K.x, y0, jx, y0 + dy, '#3e3848'); lineC(K.x - 2, y0 + 2, jx, y0 + 2 + dy, '#2c2838');   // top and bottom chords
  ctx.fillStyle = '#2c2838';
  const n = Math.max(1, Math.floor((K.x - jx) / 4));
  for (let k = 0; k < n; k++) { const x = K.x - 2 - k * 4, yy = y0 + Math.round(dy * (K.x - x) / Math.max(1, K.x - jx)); ctx.fillRect(x - 1, yy + 1, 1, 1); ctx.fillRect(x - 3, yy + 1, 1, 1); }
  lineC(K.x, y0 + 1, cx, y0 + 1 - Math.round(dy / 3), '#3e3848');
  R(cx - 4, y0 + 1, 4, 4, '#26222e');                                                         // the counterweight
  for (let k = 0; k <= 7; k++) R(K.x, y0 - 1 - k, 1, 1, '#3e3848');                            // the cat head
  lineC(K.x, y0 - 8, Math.round(lerp(K.x, jx, 0.62)), y0 + Math.round(dy * 0.62), '#2a2636');
  lineC(K.x, y0 - 8, cx, y0 + 1, '#2a2636');
  // the trolley drifts along the jib, its hook hanging
  const tu = 0.35 + 0.25 * wave(t, 2, 0.1), tx = Math.round(lerp(K.x - 6, jx, tu)), ty = y0 + 2 + Math.round(dy * tu);
  R(tx - 1, ty + 1, 3, 1, '#3a3446');
  for (let y = ty + 2; y < ty + 28; y++) R(tx, y, 1, 1, '#22202c');
  R(tx - 1, ty + 28, 3, 2, '#c8a040');
  // red aircraft lamps on both cranes, pulsing slowly together
  const a = 0.55 + 0.45 * Math.max(0, wave(t, 160));
  for (const [x, y] of [[K.x, y0 - 9], [jx, y0 + dy], [cx - 2, y0], [CRANE2.x, CRANE2.top - 8], [CRANE2.x - 38, CRANE2.top]]) {
    R(x, y, 1, 1, a > 0.7 ? '#ff6a5a' : '#c83a34'); halo(HL.red, x, y, a);
  }
}

/* ================= Swifts round the spires ================= */
const SWIFTS = Array.from({ length: 8 }, (_, i) => {
  const r = rng(500 + i);
  return { i, k: 9 + Math.floor(r() * 7), ph: r(), rx: 26 + r() * 46, ry: 8 + r() * 16, cy: 54 + r() * 30, cx: 236 + (r() - 0.5) * 30, dir: r() < 0.5 ? 1 : -1, k2: 17 + Math.floor(r() * 12) };
});
const SWIFT_SPR = [['1.1', '.1.'], ['...', '111'], ['1.1', '.1.']];
function drawSwifts(t) {
  const wide = 1 + 0.5 * smooth(BELLS[0], BELLS[0] + 4, t) * (1 - smooth(140, 158, t));   // the bells send them wider
  for (const s of SWIFTS) {
    const an = TAU * (s.k * t / LOOP + s.ph) * s.dir;
    const x = Math.round(s.cx + Math.cos(an) * s.rx * wide), y = Math.round(s.cy + Math.sin(an) * s.ry * wide + 3 * wave(t, s.k2, s.ph));
    const litNow = y > 50 && Math.abs(x - JX) < 60;
    const fr = SWIFT_SPR[(Math.floor(t * 6) + s.i) % 3];
    ctx.fillStyle = litNow ? '#d8b078' : '#120e1c';
    fr.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') ctx.fillRect(x - 1 + i, y + j, 1, 1); });
  }
}

/* ================= The basilica's lights, per frame ================= */
function bellLevel(t) {
  let b = 0;
  for (const tb of BELLS) { const dt = t - tb; if (dt >= 0 && dt < 3.4) b = Math.max(b, Math.exp(-dt * 1.2) * (1 - dt / 3.4)); }
  return b;
}
function drawBasilica(t) {
  const breathe = 0.5 + 0.5 * wave(t, 4);
  halo(HL.floodW, 210, 132, 0.75 + 0.25 * breathe); halo(HL.floodW, 270, 132, 0.75 + 0.25 * breathe);
  halo(HL.floodC, JX, 64, 0.7 + 0.3 * breathe); halo(HL.floodC, MARY.x, 74, 0.6 + 0.2 * breathe);
  blit(BAS.c);
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = 0.05 + 0.08 * breathe; ctx.drawImage(BAS.glow.c, BAS.glow.x, BAS.glow.y);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  // glints on the trencadis and the fruit
  const st = step(t, 480);
  for (const s of SPARKS) if (hash(s.id, st, 51) < 0.05) R(s.x, s.y, 1, 1, s.fruit ? '#fff0c0' : '#ffffff');
  // the cross on the Tower of Jesus, and the star of the Tower of Mary, pulsing gently
  halo(HL.cross, JX, CROSS_TOP + 5, 0.75 + 0.15 * wave(t, 6, 0.3));
  for (const E of EVANG) halo(HL.evang, E.x, E.tip, E.far ? 0.55 : 0.75);
  const sa = 0.7 + 0.3 * wave(t, 8), sx = MARY.x - 1, sy = MARY.tip - 4;
  halo(HL.star, sx, sy, sa);
  R(sx - 1, sy - 1, 3, 3, '#f4f8ff'); R(sx, sy, 1, 1, '#ffffff');
  ctx.globalAlpha = sa;
  for (const [dx, dy] of [[0, -3], [0, -2], [0, 2], [0, 3], [-3, 0], [-2, 0], [2, 0], [3, 0], [-2, -2], [2, -2], [-2, 2], [2, 2]]) R(sx + dx, sy + dy, 1, 1, '#cfe0ff');
  ctx.globalAlpha = 1;
  // the bells: their belfries glow on each stroke, faint rings go out
  const b = bellLevel(t);
  if (b > 0.02) for (const N of NATIVITY.slice(1, 3)) {
    ctx.globalAlpha = b; R(N.x - 2, N.tip + 26, 4, 6, '#ffd890'); ctx.globalAlpha = 1;
    halo(HL.bell, N.x, N.tip + 29, b);
  }
  for (const tb of BELLS) {
    const dt = t - tb;
    if (dt < 0 || dt > 2.6) continue;
    const rr = 6 + dt * 14, n = Math.floor(rr * 2.4);
    ctx.fillStyle = '#ffe2a0'; ctx.globalAlpha = (1 - dt / 2.6) * 0.32;
    for (let j = 0; j < n; j++) { const an = j / n * TAU; ctx.fillRect(Math.round(240 + Math.cos(an) * rr * 1.3), Math.round(90 + Math.sin(an) * rr * 0.7), 1, 1); }
    ctx.globalAlpha = 1;
  }
}

/* ================= Carrer de la Marina and the plaza round the pond ================= */
const MARINA_LAMPS = [128, 196, 284, 352, 424];
const PALMS = [{ x: 178, top: 128, lean: -1 }, { x: 304, top: 126, lean: 1 }];
const LEAFY = ['#0c1410', '#121e16', '#1a2a1c', '#243824', '#34482a', '#4e5a2c', '#6e6a32'];
const pondInset = y => { const yy = (y - (P0 + P1 - 1) / 2) / ((P1 - P0) / 2); return 16 * (1 - Math.sqrt(Math.max(0, 1 - yy * yy))); };
function renderPlaza() {
  const p = pix();
  // the street under the basilica: pavement, asphalt, lamp light on it
  for (let x = 0; x < W; x++) for (let y = 158; y < 172; y++) {
    let v = y < 160 ? 0.42 : y < 166 ? 0.16 : 0.34;
    for (const lx of MARINA_LAMPS) v += Math.max(0, 1 - Math.hypot((x - lx) / 18, (y - 163) / 4)) * 0.35;
    v += Math.max(0, 1 - Math.abs(x - 240) / 40) * (y >= 160 ? 0.25 : 0);                // the portal's glow on the street
    p.put(x, y, pick(['#120e18', '#1a1520', '#251d26', '#33282c', '#4a3a34', '#6e5640', '#9a7a52'], v, x, y));
    if (y === 165 && (x % 14) < 6) p.put(x, y, '#3a3236');                               // the lane marks
  }
  for (const lx of MARINA_LAMPS) { for (let y = 147; y < 160; y++) p.put(lx, y, '#1e1a24'); p.rect(lx - 1, 145, 3, 2, '#2a2430'); p.put(lx, 147, '#fff0c0'); }
  // the trees of the plaza beyond the pond, lit from under by the street lamps; the middle stays open on the portal
  const r = rng(77), blobs = [];
  for (const [x0, x1, n] of [[44, 196, 22], [286, 446, 22]]) for (let k = 0; k < n; k++) {
    const x = lerp(x0, x1, r()), mid = Math.min(Math.abs(x - 190), Math.abs(x - 290));
    blobs.push({ x, y: 146 + r() * 10 + (mid < 18 ? 4 : 0), rx: 7 + r() * 9, ry: 5 + r() * 6 });
  }
  for (const b of blobs) for (let y = Math.floor(b.y - b.ry); y <= b.y + b.ry; y++) for (let x = Math.floor(b.x - b.rx); x <= b.x + b.rx; x++) {
    const d = Math.hypot((x - b.x) / b.rx, (y - b.y) / b.ry);
    if (d > 1 - (hash(x, y, 5) - 0.5) * 0.3 || y > 166) continue;
    const under = clamp((y - (b.y - b.ry)) / (b.ry * 2), 0, 1);
    p.put(x, y, pick(LEAFY, 0.15 + under * 0.45 - d * 0.15 + (hash(x >> 1, y >> 1, 6) - 0.5) * 0.3, x, y));
  }
  for (const b of blobs) if (hash(Math.round(b.x), 1, 8) < 0.5) for (let y = Math.round(b.y + b.ry * 0.5); y < 169; y++) p.put(Math.round(b.x), y, '#1a1418');   // trunks
  for (const P of PALMS) for (let y = P.top; y < 170; y++) {                            // the palms' trunks (their crowns move)
    const x = Math.round(P.x + P.lean * Math.pow((170 - y) / (170 - P.top), 2) * 3);
    p.put(x, y, (y & 1) ? '#5a4232' : '#3e2e24'); p.put(x + 1, y, '#2a2018');
  }
  return p.flush();
}
const MARINA_CARS = [];
{ const r = rng(616);
  for (let i = 0; i < 9; i++) MARINA_CARS.push({ n: 5 + Math.floor(r() * 5), ph: r(), dir: i % 2 ? 1 : -1, id: i, col: ['#1c1a24', '#2a2a36', '#3a1e22', '#d8b830'][Math.floor(r() * 4)] }); }
function drawMarina(t) {
  for (const c of MARINA_CARS) {
    const { u } = cycle(t, c.n, c.ph), x = Math.round(c.dir > 0 ? lerp(-10, W + 10, u) : lerp(W + 10, -10, u)), y = c.dir > 0 ? 163 : 161;
    R(x - 3, y, 7, 2, c.col); R(x - 2, y - 1, 4, 1, '#14121a');
    R(x + 3 * c.dir, y + 1, 1, 1, '#fff4d8'); R(x - 3 * c.dir, y + 1, 1, 1, '#ff4a3a');
    halo(HL.car, x + 4 * c.dir, y + 1, 0.8);
  }
  for (const lx of MARINA_LAMPS) halo(HL.street, lx, 148, 0.85 + 0.1 * wave(t, 300 + lx, 0.2));
}
function drawPalms(t) {
  for (const [k, P] of PALMS.entries()) {
    const cx = P.x + P.lean * 3, cy = P.top;
    for (let f = 0; f < 10; f++) {
      const base = (f / 10) * TAU + 0.3 * k, sw = 0.12 * wave(t, 7 + f % 3, f * 0.13 + k * 0.4);
      const dx = Math.cos(base + sw), up = Math.sin(base + sw), len = 11 + (f * 7) % 4;
      for (let s = 1; s <= len; s++) {
        const q = s / len, x = Math.round(cx + dx * s * 1.1), y = Math.round(cy - up * s * 0.55 + q * q * 7);
        R(x, y, 1, 1, q < 0.4 ? '#2e3e24' : up < 0 ? '#22321e' : '#46602c');
        if (q > 0.25) R(x, y + 1, 1, 1, (s & 1) ? '#1a2618' : '#2a3e22');
        if (q > 0.45 && q < 0.85 && (s & 1)) R(x, y + 2, 1, 1, '#16201a');
      }
    }
    R(cx - 1, cy - 1, 3, 2, '#4a3a26');
  }
}

/* ================= The pond: the basilica mirrored, ducks, a fish now and then ================= */
function renderWater() {
  const [c, x] = makeCanvas(W, P1 - P0), img = x.createImageData(W, P1 - P0), d = img.data;
  const pal = ['#07081a', '#0a0c20', '#0e1026', '#13142c', '#1a1832'].map(hex);
  for (let y = 0; y < P1 - P0; y++) for (let i = 0; i < W; i++) {
    const v = 0.2 + (y / (P1 - P0)) * 0.5 + Math.max(0, 1 - Math.abs(i - 240) / 60) * 0.3;
    const f = clamp(v, 0, 1) * (pal.length - 1); let k = Math.floor(f); if (f - k > bayer(i, y)) k++;
    const col = pal[Math.min(k, pal.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
const DUCKS = [{ cx: 150, cy: 188, rx: 54, ry: 5, k: 2, ph: 0.1, dir: 1 }, { cx: 324, cy: 184, rx: 34, ry: 4, k: 3, ph: 0.6, dir: -1 }];
const DUCK = ['..HH....', '.HHHB...', '.BBBBBBT', '..BBBBB.'];
const POND_STREAKS = [
  { x: 240, w: 6, col: '#ffc070', a: 0.2, seed: 1, y0: P0 + 1, len: 26 }, { x: 240, w: 2, col: '#f4f4ff', a: 0.16, seed: 2, y0: P0 + 2, len: 18 },
  ...MARINA_LAMPS.map((x, i) => ({ x, w: 2, col: '#ffd890', a: 0.28, seed: 10 + i, y0: P0 + 1, len: 16 })),
  ...LAMPS.map((l, i) => ({ x: l.x, w: 3, col: '#ffd890', a: 0.26, seed: 20 + i, y0: P0 + 12, len: 18 })),
  { x: 210, w: 4, col: '#ffb860', a: 0.14, seed: 30, y0: P0 + 1, len: 22 }, { x: 270, w: 4, col: '#ffb860', a: 0.14, seed: 31, y0: P0 + 1, len: 22 },
];
function drawPond(t) {
  ctx.drawImage(WATER_C, 0, P0);
  reflect(ctx, t, { top: P0, rows: P1 - P0, squash: 2.3, alpha: 0.58, k1: 45, k2: 110, amp: 0.5, grow: 0.04, x: PX0 - 4, w: PX1 - PX0 + 8 });
  for (let y = P0 + 2, n = 0; y < P1; y += 2, n++) for (let j = 0; j < 4; j++) {     // drifting ripple troughs and glints
    const k = 2 + ((n * 7 + j * 5) % 7), len = 4 + Math.floor(hash(n, j, 41) * 14);
    const x = Math.round(PX0 + frac(hash(n, j, 40) + k * t / LOOP) * (PX1 - PX0));
    ctx.globalAlpha = j === 3 ? 0.28 : 0.4;
    R(x, y + (j & 1), len, 1, j === 3 ? '#8a90c0' : '#050612');
  }
  ctx.globalAlpha = 1;
  for (const s of POND_STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, s.y0, Math.min(P1, s.y0 + s.len));
  // a fish rises: rings spread and fade
  for (const [i, ts] of SPLASH.entries()) {
    const dt = t - ts;
    if (dt < -0.2 || dt > 4) continue;
    const X = i ? 300 : 198, Y = i ? 186 : 190;
    if (dt < 0.4 && dt > 0) R(X, Y - 1 - Math.round(Math.sin(dt / 0.4 * Math.PI) * 2), 1, 1, '#d8dcf0');
    for (const lag of [0, 0.8]) {
      const q = dt - lag; if (q < 0) continue;
      const rr = 1 + q * 4, n = Math.max(6, Math.floor(rr * 3));
      ctx.fillStyle = '#9aa2d0'; ctx.globalAlpha = 0.6 * (1 - q / 4);
      for (let k = 0; k < n; k++) { const an = k / n * TAU; ctx.fillRect(Math.round(X + Math.cos(an) * rr), Math.round(Y + Math.sin(an) * rr * 0.32), 1, 1); }
    }
    ctx.globalAlpha = 1;
  }
  // the ducks, slow loops with a little wake
  for (const [i, D] of DUCKS.entries()) {
    const an = TAU * (D.k * t / LOOP + D.ph) * D.dir, x = Math.round(D.cx + Math.cos(an) * D.rx), y = Math.round(D.cy + Math.sin(an) * D.ry);
    const right = -Math.sin(an) * D.dir > 0;
    for (let k = 1; k <= 5; k++) { ctx.globalAlpha = 0.35 * (1 - k / 6); R(right ? x - 1 - k * 2 : x + 8 + k * 2, y + 3 + (k >> 1), 2, 1, '#8a8ec0'); R(right ? x - 1 - k * 2 : x + 8 + k * 2, y + 3 - (k >> 1), 1, 1, '#6a6ea0'); }
    ctx.globalAlpha = 1;
    ctx.drawImage(DUCK_C[i][right ? 0 : 1], x, y);
    ctx.globalAlpha = 0.3; ctx.drawImage(DUCK_C[i][right ? 0 : 1], 0, 2, 8, 2, x, y + 4, 8, 2); ctx.globalAlpha = 1;
  }
}

/* ================= The ground: the pond's rim, the sandy walk, the kerb, the bike lane, Lepant, the pavement ================= */
const SAND = ['#16121a', '#1e1820', '#282026', '#33292a', '#41332e', '#544034', '#6c523e', '#8a6a4c'];
const ASPH = ['#0e0c14', '#14121a', '#1b1820', '#242026', '#2e282c', '#3c3234'];
function groundLight(x, y) {                          // warm pools of lamplight: the plaza lamps, the terrace, the bar
  let v = 0;
  for (const l of LAMPS) v += Math.max(0, 1 - Math.hypot((x - l.x) / 46, (y - 214) / 18)) * 0.5;
  v += Math.max(0, 1 - Math.hypot((x - 410) / 60, (y - 224) / 20)) * 0.45;
  v += Math.max(0, 1 - Math.hypot((x - DOOR.x) / 30, (y - 236) / 14)) * 0.35;
  return v;
}
function renderGround() {
  const p = pix();
  for (let y = 166; y < H; y++) for (let x = 0; x < W; x++) {
    const inPond = y >= P0 && y < P1 && x >= PX0 + pondInset(y) && x < PX1 - pondInset(y);
    if (inPond) continue;
    const g = groundLight(x, y);
    let col;
    if (y < 204) {                                                                      // round the pond: beds and sand
      const near = y >= P1 && y < P1 + 4, rim = !near && y >= P0 - 3 && y < P1 && (x >= PX0 + pondInset(clamp(y, P0, P1 - 1)) - 3 && x < PX1 - pondInset(clamp(y, P0, P1 - 1)) + 3);
      if (near) col = y === P1 ? '#b8a080' : y === P1 + 1 ? '#8a7660' : pick(['#3a302c', '#4e4038', '#62503e'], 0.3 + g, x, y);
      else if (rim) col = y === P0 - 3 ? '#8a7866' : pick(['#3a302c', '#4e4038'], 0.5, x, y);
      else if (y < 172) col = null;
      else col = hash(x >> 1, y >> 1, 3) < 0.55 ? pick(LEAFY, 0.25 + g * 0.6 + (hash(x, y, 4) - 0.5) * 0.3, x, y) : pick(SAND, 0.3 + g, x, y);
      if (col && !near && !rim && hash(x, y, 5) < 0.015) col = hash(x, y, 6) < 0.5 ? '#c84a5a' : '#e8d070';   // flowers in the beds
    } else if (y < KERB) {                                                              // the sandy walk (sauló)
      col = pick(SAND, 0.32 + g + (y - 204) * 0.006 + (hash(x, y, 7) - 0.5) * 0.12, x, y);
      if (hash(x, y, 8) < 0.008) col = '#8a7460';
    } else if (y < KERB + 3) col = y === KERB ? '#a89478' : y === KERB + 1 ? '#6a5a4c' : '#2a2228';
    else if (y < 245) {                                                                 // the bike lane
      col = pick(['#0c1614', '#12201c', '#182a24', '#20342c', '#2a4034'], 0.35 + g * 0.6 + (hash(x, y, 9) - 0.5) * 0.15, x, y);
      if (y === 244) col = '#6a7a72';
    } else if (y < 262) {                                                               // Carrer de Lepant
      col = pick(ASPH, 0.25 + g * 0.5 + (y - 245) * 0.015 + (hash(x, y, 10) - 0.5) * 0.14, x, y);
      if ((y === 253 || y === 254) && (x % 24) < 12) col = '#4e4c56';
    } else if (y < 264) col = y === 262 ? '#8a8078' : '#4a4044';
    else {                                                                              // panots: the flower tile
      const tx = x % 8, ty = (y - 264) % 6;
      col = (tx === 0 || ty === 0) ? '#2a262c' : ((tx === 2 || tx === 5) && (ty === 2 || ty === 4)) || (tx >= 3 && tx <= 4 && ty === 3) ? '#4a4448' : '#3a3438';
    }
    if (col) p.put(x, y, col);
  }
  // bike stencils on the lane
  for (const bx of [150, 420]) for (const [i, j] of [[0, 2], [1, 1], [1, 3], [2, 2], [3, 2], [4, 1], [5, 1], [6, 2], [5, 3], [7, 2], [8, 1], [8, 3], [9, 2], [4, 0]]) p.put(bx + i, 237 + j, '#8a8a92');
  // fallen leaves on the walk
  const r = rng(3);
  for (let k = 0; k < 18; k++) { const x = Math.floor(r() * W), y = 205 + Math.floor(r() * 27); p.put(x, y, ['#6a5226', '#7a6030', '#5a4a24'][k % 3]); if (k & 1) p.put(x + 1, y, '#5a4220'); }
  // the plaza's lamp posts: cast iron, a lantern on top
  for (const l of LAMPS) {
    const fy = WALK0 + 8;
    for (let y = l.y + 4; y < fy; y++) { p.put(l.x, y, '#1e2620'); p.put(l.x + 1, y, y > fy - 6 ? '#2e382c' : '#141a16'); }
    p.rect(l.x - 1, fy - 3, 4, 3, '#1a201a'); p.rect(l.x - 1, l.y + 4, 4, 1, '#2a3428');
    p.rect(l.x - 2, l.y - 2, 6, 6, '#ffe0a0'); p.rect(l.x - 1, l.y - 1, 4, 4, '#fff4d0');
    p.rect(l.x - 3, l.y - 4, 8, 2, '#1a201a'); p.put(l.x, l.y - 5, '#1a201a'); p.put(l.x + 1, l.y - 5, '#1a201a');
    p.put(l.x - 2, l.y + 3, '#1a201a'); p.put(l.x + 3, l.y + 3, '#1a201a');
  }
  return p.flush();
}

/* ================= People ================= */
const SKINS = ['#f2c8a0', '#e0a87c', '#c08858', '#8a5a3a'], HAIRS = ['#1a1210', '#3a2216', '#6a4020', '#c8a058', '#8a3a1a', '#cfcac4'];
const NIGHT = '#141428', GLOWC = '#ffd898';
/** a look: proportions for a height of h px and summer colours dimmed for the night (dk). Extras: long (rows of long
    hair), dress (rows of the top's colour over the legs), shorts (rows of trouser before bare legs), hat, bag, apron. */
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.26, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : 4, th = Math.round(h * 0.38);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 22 ? 7 : h >= 19 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.35), top: m(top), topD: mixHex(top, NIGHT, dk + 0.28),
    topL: mixHex(top, GLOWC, 0.42), bot: m(bot), shoe: m(o.shoe || '#1a1418'), long: o.long || 0, dress: o.dress || 0, shorts: o.shorts || 0,
    hat: o.hat ? m(o.hat) : null, hatL: o.hat ? mixHex(o.hat, GLOWC, 0.35) : null, bag: o.bag ? m(o.bag) : null, apron: o.apron ? m(o.apron) : null };
}
/**
 * One person, feet on row fy, centred on column x. Returns the row of the top of the head.
 * o.view: 0 from behind (looking at the basilica), 1 facing us, 2 in profile (o.dir: 1 right, -1 left)
 * o.step: stride frame, o.sit: seated (feet on fy, seat half the legs up), o.lean: head px towards a neighbour
 * o.aL / o.aR: 0 down, 1 raised and pointing, 2 a phone up, 3 out, 4 bent (holding), 5 a tray held up, 6 hand to the
 * mouth, 7 clapping, 9 an arm round the neighbour; o.pedal: legs on the pedals (0 or 1)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const seat = o.sit ? Math.round(P.ll * 0.5) : 0, legs = P.ll, bw = side ? tw - 2 : tw, tx = x - (bw >> 1);
  const ty = (o.sit ? fy - seat - th + 1 : fy - legs - th + 1) + (o.pedal !== undefined ? 0 : 0), hy = ty - hh, ly = ty + th;
  if (!o.sit && o.pedal === undefined) R(tx - 1, fy + 1, bw + 3, 1, 'rgba(8,8,20,0.3)');           // a shadow on the sand
  const legCol = (j) => (P.shorts && j >= P.shorts ? P.skin : P.bot);
  const legRun = (lx, y0, n, w) => { for (let j = 0; j < n; j++) R(lx, y0 + j, w, 1, j < P.dress ? P.top : legCol(j)); };
  if (o.pedal !== undefined) {                                                       // riding: thigh forward, shin down to the pedal
    const fx = x + dir * 3, up = o.pedal ? 1 : 0;
    R(Math.min(x, fx), ly, 4, 2, P.bot);
    R(fx - (dir < 0 ? 1 : 0), ly + 2 - up, 2, 4, legCol(4)); R(fx + dir, ly + 5 - up, 2, 1, P.shoe);
    R(x - dir, ly + 2, 2, 3 + up, legCol(4)); R(x - dir, ly + 5 + up, 2, 1, P.shoe);
  } else if (o.sit) {
    if (view === 2) { R(dir > 0 ? x - 1 : x - 4, ly, 5, 2, P.dress ? P.top : P.bot); R(x + dir * 3 - (dir < 0 ? 1 : 0), ly + 2, 2, seat - 1, legCol(3)); R(x + dir * 3 + (dir > 0 ? 0 : -1), fy, 3, 1, P.shoe); }
    else if (view === 1) { R(tx, ly, bw, 2, P.dress ? P.top : P.bot); const lw = bw >= 7 ? 3 : 2; legRun(tx, ly + 2, seat - 1, lw); legRun(tx + bw - lw, ly + 2, seat - 1, lw); R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe); }
  } else if (side) {
    if (o.step) {
      legRun(dir > 0 ? x : x - 1, ly, legs - 1, 2); R(dir > 0 ? x + 1 : x - 2, fy, 2, 1, P.shoe);
      legRun(dir > 0 ? x - 2 : x + 1, ly, legs - 2, 2); R(dir > 0 ? x - 3 : x + 2, fy - 1, 2, 1, P.shoe);
    } else { legRun(x - 1, ly, legs, 2); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
  } else {
    const lw = bw >= 7 ? 3 : 2;
    R(tx, ly, bw, 2, P.dress ? P.top : P.bot); legRun(tx, ly + 2, legs - 2, lw); legRun(tx + bw - lw, ly + 2, legs - 2, lw);
    if (P.dress > 2) R(tx - 1, ly + P.dress - 2, bw + 2, 2, P.top);                  // the hem flares
    R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
  }
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);                                                         // the lamps' light on the shoulders
  if (P.apron) { if (view === 1) R(tx + 1, ty + th - 2, bw - 2, Math.min(legs, 8), P.apron); else if (side) R(dir > 0 ? x : x - 2, ty + th - 2, 3, Math.min(legs, 8), P.apron); }
  if (P.bag) { if (view === 0) R(tx + 1, ty + 2, bw - 2, th - 3, P.bag); else if (side) R(dir > 0 ? tx - 1 : tx + bw - 1, ty + 2, 2, th - 3, P.bag); }
  const sleeve = (ax, y, n) => { R(ax, y, 1, Math.min(2, n), P.topD); if (n > 2) R(ax, y + 2, 1, n - 2, P.skin); };
  if (side) {
    const st = o.aR || 0;
    if (st === 0) { const ax = x + (o.step ? -dir : 0); sleeve(ax, ty + 1, th - 1); }
    else if (st === 1) { for (let k = 0; k < 4; k++) R(x + dir * (1 + k), ty + 1 - k, 1, 1, k < 1 ? P.topD : P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k), ty + 1 - k, 1, 1, k < 1 ? P.topD : P.skin); R(x + dir * 4 - (dir < 0 ? 1 : 0), ty - 3, 2, 3, '#0e1018'); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { R(x + dir, ty + 2, 1, 2, P.topD); R(x + dir * 2, ty + 3, 1, 1, P.skin); }
    else if (st === 5) { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty - 1, 1, 2, P.skin); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) sleeve(ax, ty + 1, th - 1);
      else if (st === 1) { for (let k = 0; k < 4; k++) R(ax + sg * k, ty + 1 - k, 1, 1, k < 1 ? P.topD : P.skin); }
      else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.skin); const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 1, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 1, 2, 1, '#ffe2a8'); R(p0, hy, 2, 1, '#8ab8e8'); } }
      else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 1, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 1, 1, 1, P.skin); }
      else if (st === 4) { R(ax, ty + 1, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), ty + 3, 1, 1, P.skin); }
      else if (st === 5) { R(ax, ty + 1, 1, 1, P.topD); R(ax + sg, ty, 1, 1, P.topD); R(ax + sg, hy + 1, 1, ty - hy - 1, P.skin); }
      else if (st === 6) { R(ax, ty + 1, 1, 2, P.topD); R(ax - sg, ty, 1, 1, P.skin); R(x - sg, hy + hh - 1, 1, 1, P.skin); }
      else if (st === 7) { R(ax, ty + 1, 1, 2, P.topD); R(ax - sg, ty + 2, 1, 1, P.skin); }
      else if (st === 8) { R(ax, ty + 1, 1, 2, P.topD); R(ax + sg, ty + 2, 1, 1, P.skin); }
      else { R(sg < 0 ? ax - 3 : ax, ty + 1, 4, 1, P.topD); }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const hx = x - (hw >> 1) + (o.lean || 0);
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, 1, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, 1, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx - 1, hy, hw + 2, 1, P.hat); R(hx, hy - 1, hw, 1, P.hatL); }
  return hy;
}
const WHO = {
  guitar: look(21, 2, 0, '#3e5c8a', '#2a2a34', { hat: '#2a2430' }),
  he: look(21, 1, 1, '#d8d0c0', '#2a3046'), she: look(20, 0, 3, '#c84a5a', '#2a2a34', { long: 3 }),
  aud1: look(21, 0, 2, '#6a8a5a', '#3a3430'), aud2: look(20, 3, 0, '#e8d8b0', '#2a3046', { long: 2 }),
  aud3: look(22, 1, 1, '#2f4a72', '#1e1e28'), kid: look(13, 1, 3, '#e8b040', '#3a4a7a', { dk: 0.2 }),
  aud4: look(21, 2, 4, '#a0485a', '#2a2a34', { dress: 5, long: 2 }),
  waiter: look(22, 1, 0, '#f0ece4', '#16141a', { apron: '#141218', dk: 0.18 }),
  g1: look(20, 0, 4, '#d86a3a', '#2a3046', { long: 2 }), g2: look(21, 2, 0, '#2a6a6a', '#3a3430'),
  g3: look(21, 1, 5, '#8a7a5a', '#2a2a34'), g4: look(20, 0, 3, '#e8e0d0', '#3a2a4a', { long: 3 }),
  g5: look(20, 3, 1, '#c8a040', '#2a3046'), g6: look(20, 0, 0, '#5a4a8a', '#2a2a34', { long: 2 }),
  nbr: look(21, 0, 5, '#7a5a8a', '#4a3a5a', { dress: 6 }), old: look(21, 1, 5, '#e8e4dc', '#3a3a44'),
  dogw: look(23, 1, 2, '#b84a3a', '#2a3046'), armA: look(22, 0, 1, '#e8e0d0', '#3a4a6a'), armB: look(21, 2, 4, '#3a7a6a', '#2a2a34', { dress: 5, long: 2 }),
  jog: look(23, 1, 0, '#e86a4a', '#1e1e28', { shorts: 3 }), cycA: look(22, 0, 3, '#f0d040', '#2a3046'), cycB: look(22, 1, 1, '#4a8ac8', '#1e1e28', { shorts: 3 }),
  taker: look(22, 2, 0, '#8a3a5a', '#2a3046', { bag: '#3a3430' }), bringer: look(22, 0, 2, '#5a6a3a', '#2a2a34'),
  tour: look(22, 0, 3, '#f0f0e8', '#4a5a7a', { hat: '#d8c8a0', bag: '#4a4038' }), pal1: look(21, 3, 0, '#3a3a4a', '#2a2a34'), pal2: look(20, 1, 2, '#c8b060', '#2a3046', { long: 2 }),
};

/* ================= Sprites: bikes, the taxi, the moped, the dog, the cat, the ducks ================= */
const SHARE = { F: '#d8d4cc', R: '#c83a34', K: '#16141c', B: '#6a6670', G: '#2a2830' };
function renderShareBike() {                          // side view, heading right: white frame, red guards, a basket
  const p = pix(18, 11);
  for (const cx of [3, 14]) for (let j = -3; j <= 3; j++) for (let i = -3; i <= 3; i++) if (Math.abs(Math.hypot(i, j) - 2.9) < 0.6) p.put(cx + i, 7 + j, j < -1 ? '#4a4652' : '#121018');
  for (const [a, b, c, d] of [[3, 7, 8, 7], [8, 7, 6, 3], [6, 3, 12, 3], [8, 7, 12, 3], [12, 3, 14, 7], [12, 3, 12, 1]]) line(p, a, b, c, d, SHARE.F);
  p.rect(5, 2, 3, 1, SHARE.K); p.rect(11, 0, 3, 1, SHARE.G);
  p.rect(14, 1, 3, 3, SHARE.B); p.put(15, 2, '#8a8690');                    // the basket
  p.put(1, 4, SHARE.R); p.put(2, 4, SHARE.R); p.put(15, 4, SHARE.R); p.put(16, 5, SHARE.R);   // mudguards
  p.put(3, 7, '#8a8690'); p.put(14, 7, '#8a8690');
  return p.flush();
}
function renderTaxi(free) {                           // a Barcelona taxi: black, yellow doors, the roof lamp
  const w = 60, h = 21, p = pix(w, h), Y = '#f2c230', Yd = '#b88a1c', K = '#141218', Kl = '#6a6878';
  for (let x = 2; x < w - 2; x++) {
    const cab = x > 15 && x < 44, roof = cab ? (x < 20 ? 6 + (20 - x) : x > 39 ? 6 + (x - 39) : 6) : 11;
    for (let y = roof; y < 17; y++) {
      let col = y === roof ? Kl : y === 15 ? '#22202a' : K;
      if (cab && y > roof && y < 11 && x > 16 && x < 43 && x !== 29 && x !== 30) col = (x + y) % 5 === 0 ? '#4a5a78' : '#1e2638';   // glass
      else if (y >= 11 && ((x >= 17 && x <= 28) || (x >= 31 && x <= 42))) col = y === 11 ? '#ffd860' : y > 15 ? Yd : Y;          // the yellow doors
      else if (y === 11) col = '#3a3846';
      if (y === 13 && (x === 27 || x === 40)) col = '#2a2418';
      p.put(x, y, col);
    }
  }
  p.rect(0, 12, 2, 4, K); p.rect(w - 2, 12, 2, 4, K);
  p.rect(w - 3, 12, 3, 2, '#fff6dc'); p.rect(0, 12, 2, 2, '#ff3a2a');                // head lamp (front right), tail lamp
  for (const cx of [11, 48]) for (let j = -4; j <= 4; j++) for (let i = -4; i <= 4; i++) {
    const d = Math.hypot(i, j); if (d > 4.3) continue;
    p.put(cx + i, 16 + j, d > 3 ? '#08080c' : d > 1.6 ? '#3a3a44' : '#8a8a96');
  }
  p.rect(26, 4, 8, 2, '#1a1820'); p.rect(27, 4, 6, 1, free ? '#4aff8a' : '#ff4a3a');   // the roof sign
  p.rect(36, 7, 3, 4, '#0a0a10'); if (!free) p.rect(22, 7, 3, 4, '#0a0a10');          // the driver, a passenger
  return p.flush();
}
function renderMoped() {                              // a scooter and its rider in a helmet, heading right
  const p = pix(26, 22);
  for (const cx of [5, 20]) for (let j = -3; j <= 3; j++) for (let i = -3; i <= 3; i++) { const d = Math.hypot(i, j); if (d < 3.4) p.put(cx + i, 18 + j, d > 2.2 ? '#0a0a10' : '#5a5a66'); }
  for (let x = 3; x < 22; x++) for (let y = 13; y < 17; y++) if (!(x > 8 && x < 15 && y < 15)) p.put(x, y, y === 13 ? '#e8e4e0' : '#b8b4b0');
  p.rect(18, 7, 2, 7, '#8a8690'); p.rect(17, 7, 4, 1, '#2a2830'); p.rect(21, 9, 2, 2, '#fff6dc');
  p.rect(9, 6, 5, 7, '#2a3a5a'); p.rect(12, 9, 6, 1, '#2a3a5a'); p.rect(9, 13, 6, 2, '#1e1e28'); p.rect(13, 15, 2, 3, '#1e1e28');
  p.rect(9, 1, 5, 5, '#d83a3a'); p.rect(12, 2, 2, 2, '#1a1a24'); p.put(9, 1, '#f08a8a');
  p.put(2, 13, '#ff3a2a');
  return p.flush();
}
const DOGS_SPR = [['......HH', 'T.BBBBHH', '.BBBBBB.', '.B....B.', '.L....L.'], ['......HH', 'T.BBBBHH', '.BBBBBB.', '..B..B..', '.L....L.']];
const DOG_COL = { B: '#c8a878', H: '#d8b888', T: '#c8a878', L: '#8a6a48' };
const CAT_SIT = ['.K.K', '.KKK', '.KKW', 'KKKK', 'KKKK'], CAT_WALK = [['....K.K.', 'T...KKK.', '.KKKKKW.', '.K.K.K..'], ['....K.K.', 'T...KKK.', '.KKKKKW.', 'K....K..']];
const CAT_COL = { K: '#1a1618', W: '#d8c8b0', T: '#1a1618' };

/* ================= The corner blocks at our hands, the plane trees, the bar ================= */
const STONE = ['#140f16', '#1c151c', '#261c22', '#322428', '#402e2e', '#523c36', '#6a4e40', '#866448'];
const IRON = '#0c0a10', IRONL = '#3a3038';
const LFLOORS = [44, 86, 128, 170];                  // the left block's balcony floors
const RFLOORS = [62, 104, 146];                       // the right block's
function railing(p, x0, x1, fy) {                     // a wrought-iron balcony rail with its curls, and the slab
  for (let x = x0; x <= x1; x++) {
    p.put(x, fy - 9, IRONL); p.put(x, fy - 8, IRON);
    const m = (x - x0) % 6;
    if (m === 0) for (let y = fy - 7; y < fy; y++) p.put(x, y, IRON);
    else if (m === 2 || m === 4) { p.put(x, fy - 6, IRON); p.put(x, fy - 2, IRON); }
    else if (m === 3) { p.put(x, fy - 5, IRON); p.put(x, fy - 3, IRON); p.put(x, fy - 7, IRON); }
    p.put(x, fy, '#5a4a44'); p.put(x, fy + 1, '#2a2026');
  }
  for (let x = x0 + 1; x < x1; x += 3) p.put(x, fy + 2, '#1a1418');                  // the slab's brackets
}
function renderLeftBlock() {
  const p = pix();
  for (let x = 0; x < LBLD; x++) for (let y = 0; y < 263; y++) {
    const ch = x >= 36, lamp = Math.max(0, 1 - Math.hypot((x - 104) / 90, (y - 160) / 90)) * 0.45;
    let v = 0.22 + lamp + (ch ? -0.08 : 0) + y / 263 * 0.12 + (hash(x, y, 21) - 0.5) * 0.08;
    if (y % 42 === 14 || x === 36) v -= 0.1;                                       // string courses, the chamfer's edge
    p.put(x, y, pick(STONE, v, x, y));
  }
  for (const fy of LFLOORS) {
    for (let y = fy - 30; y < fy; y++) for (let x = 12; x < 26; x++) p.put(x, y, x === 18 || y === fy - 30 ? '#1a1418' : '#0e0c14');   // French doors (lit per frame)
    for (let y = fy - 30; y < fy; y++) for (const sx of [7, 27]) for (let i = 0; i < 4; i++) p.put(sx + i, y, (y & 1) ? '#24382c' : '#33503c');   // open green shutters
    for (let y = fy - 26; y < fy - 6; y++) for (let x = 39; x < 43; x++) p.put(x, y, (y & 1) ? '#1e3026' : '#2a4232');   // the chamfer's shut window
    railing(p, 2, 34, fy);
    for (let x = 0; x < 36; x++) { p.put(x, fy - 34, '#6a5246'); p.put(x, fy - 33, '#3a2c2c'); }   // the lintel moulding
  }
  // the ground floor: a shop's rolled-down shutter, the house door with its lit glass
  for (let y = 184; y < 262; y++) for (let x = 2; x < 26; x++) p.put(x, y, y === 184 ? '#3a3440' : (y & 1) ? '#2a2632' : '#34303c');
  for (const [i, j, c] of [[6, 220, '#7a4a6a'], [7, 219, '#7a4a6a'], [8, 220, '#4a7a8a'], [12, 222, '#7a4a6a'], [13, 221, '#c8a040'], [16, 220, '#4a7a8a'], [17, 221, '#4a7a8a']]) p.rect(i, j, 2, 3, c);   // a tag on it
  for (let y = 204; y < 262; y++) for (let x = 30; x < 42; x++) p.put(x, y, y < 210 ? (x + y) % 3 ? '#d8a050' : '#8a5a2a' : x === 30 || x === 41 ? '#2a2026' : y < 236 ? '#5a3a20' : '#3a2618');
  return p.flush();
}
function renderRightBlock() {
  const p = pix();
  for (let x = RBLD; x < W; x++) for (let y = 0; y < KERB; y++) {
    const lamp = Math.max(0, 1 - Math.hypot((x - 400) / 80, (y - 200) / 60)) * 0.4;
    let v = 0.2 + lamp + y / KERB * 0.14 + (x < RBLD + 6 ? -0.1 : 0) + (hash(x, y, 22) - 0.5) * 0.08;
    if (y % 42 === 20) v -= 0.1;
    p.put(x, y, pick(STONE, v, x, y));
  }
  for (const fy of RFLOORS) {
    for (let y = fy - 30; y < fy; y++) for (let x = 458; x < 472; x++) p.put(x, y, x === 465 || y === fy - 30 ? '#1a1418' : '#0e0c14');
    for (let y = fy - 30; y < fy; y++) for (let i = 0; i < 4; i++) { p.put(453 + i, y, (y & 1) ? '#24382c' : '#33503c'); p.put(473 + i, y, (y & 1) ? '#24382c' : '#33503c'); }
    railing(p, 450, 479, fy);
  }
  // the bar: an awning, a lit room with bottles behind the counter, the sign
  for (let y = 196; y < KERB; y++) for (let x = RBLD + 4; x < 476; x++) {
    let col = y < 200 ? '#2a1e1c' : x >= 452 && x < 464 ? (y < 204 ? '#fff0c8' : '#e8a858') : '#c87a3a';
    if (x >= 452 && x < 464) { /* the open door */ }
    else if (y > 204 && y < 214 && (x % 3 === 0)) col = ['#5a8a4a', '#8a3a2a', '#d8b860', '#3a5a7a'][x % 4];   // bottles on the shelves
    else if (y === 214 || y === 204) col = '#6a3a1c';
    else if (y > 220) col = y === 221 ? '#8a5a30' : '#4a2a18';                                          // the counter
    p.put(x, y, col);
  }
  for (let x = RBLD; x < W; x++) for (let y = 186; y < 196; y++) p.put(x, y, y === 195 ? '#4a1e1a' : ((x >> 2) & 1) ? '#b83a2a' : '#e8dcc0');   // the striped awning
  for (let x = RBLD; x < W; x += 4) p.put(x + 1, 196, ((x >> 2) & 1) ? '#b83a2a' : '#e8dcc0');
  for (let y = 172; y < 184; y++) for (let x = RBLD + 4; x < 476; x++) p.put(x, y, y === 172 || y === 183 ? '#3a2a20' : '#1c1416');   // the sign board
  const c = p.flush();
  text(p.x, 'BAR', RBLD + 12, 173, '#f2b134'); text(p.x, 'TAPES', RBLD + 8, 179, '#e8dcc0');
  return c;
}
const BARK = ['#2a2620', '#3e3a2c', '#5a5640', '#7a7656', '#9a9470'];
function renderTrees() {
  const out = [];
  for (const [ti, T] of [TREE_L, TREE_R].entries()) {
    const p = pix(), r = rng(400 + ti), top = ti ? 70 : 118;
    for (let y = top; y <= T.y; y++) {                                           // the trunk: mottled plane-tree bark
      const hw = 2.5 + (y - top) / (T.y - top) * 0.8 + (y > T.y - 4 ? 1 : 0);
      for (let x = Math.round(T.x - hw); x < Math.round(T.x + hw); x++) {
        const a = (x + 0.5 - T.x) / hw, patch = hash(x >> 1, Math.floor(y / 3), 33 + ti);
        p.put(x, y, pick(BARK, (patch < 0.3 ? 0.9 : patch < 0.6 ? 0.55 : 0.3) + a * 0.2 - Math.abs(a) * 0.2 - (1 - (y - top) / (T.y - top)) * 0.25, x, y));
      }
    }
    for (const [dx, len] of ti ? [[1, 30], [1, 40], [-1, 10], [1, 20]] : [[-1, 26], [1, 30], [1, 16]]) {   // limbs up into the canopy
      const y0 = top + Math.floor(r() * 12);
      for (let k = 0; k < len; k++) p.put(Math.round(T.x + dx * k * 0.8), Math.round(y0 - k * 0.9), k < len * 0.5 ? '#3e3a2c' : '#2a2620');
    }
    out.push(p.flush());
  }
  return out;
}
const CANOPY = [{ x: 78, y: 96, rx: 46, ry: 40, seed: 61 }, { x: 446, y: 30, rx: 62, ry: 52, seed: 62 }];
function renderCanopy(C, layer) {                    // one of three layers of leaf clumps, each swaying on its own
  const p = pix(), r = rng(C.seed * 10 + layer);
  for (let k = 0; k < 70; k++) {
    const an = r() * TAU, d = Math.sqrt(r());
    const cx = C.x + Math.cos(an) * C.rx * d, cy = C.y + Math.sin(an) * C.ry * d, rr = 3 + r() * 5;
    if (Math.floor(r() * 3) !== layer) continue;
    if (C.seed === 62 && cy > 86 && cx < 400) continue;                            // keep the billboard's lamps clear
    for (let y = Math.floor(cy - rr); y <= cy + rr; y++) for (let x = Math.floor(cx - rr); x <= cx + rr; x++) {
      const dd = Math.hypot(x - cx, (y - cy) * 1.2) / rr;
      if (dd > 1 || hash(x, y, C.seed + layer) < 0.12 * dd) continue;
      const under = (y - cy) / rr, lamp = Math.max(0, 1 - Math.hypot((x - (C.seed === 61 ? 104 : 400)) / 90, (y - 150) / 90));
      let v = 0.18 + under * 0.25 + lamp * 0.55 - dd * 0.1 + (hash(x >> 1, y >> 1, 7 + layer) - 0.5) * 0.3;
      if (hash(x, y, 70 + layer) < 0.05) v += 0.35;                               // a yellowing leaf
      p.put(x, y, pick(['#0a100c', '#101a12', '#182618', '#22341e', '#344a24', '#4e6028', '#76782e', '#a08a38'], v, x, y));
    }
  }
  return trim(p.flush());
}
// the string lights over the terrace: [x0, y0, x1, y1, sag]
const FESTOON = [[446, 178, 396, 152, 7], [395, 152, 342, 160, 8], [446, 190, 396, 168, 6]];
const BULBS = [];
FESTOON.forEach((f, k) => { const n = Math.floor(Math.hypot(f[2] - f[0], f[3] - f[1]) / 5); for (let i = 1; i < n; i++) BULBS.push({ k, s: i / n, id: BULBS.length }); });
function festoonAt(f, s, t, k) {
  const sag = f[4] + 0.8 * wave(t, 20 + k * 3, k * 0.3);
  return [Math.round(lerp(f[0], f[2], s)), Math.round(lerp(f[1], f[3], s) + sag * 4 * s * (1 - s))];
}
function drawFestoon(t) {
  FESTOON.forEach((f, k) => {
    ctx.fillStyle = '#1a1418';
    for (let s = 0; s <= 1; s += 0.02) { const [x, y] = festoonAt(f, s, t, k); ctx.fillRect(x, y, 1, 1); }
  });
  for (const b of BULBS) {
    const [x, y] = festoonAt(FESTOON[b.k], b.s, t, b.k), a = 0.82 + 0.18 * wave(t, 40 + (b.id % 7) * 11, b.id * 0.17);
    R(x, y + 1, 1, 1, b.id % 5 === 2 ? '#ffcf80' : '#fff0c0');
    if (b.id % 2 === 0) halo(HL.bulb, x, y + 1, a);
  }
}

/* ================= Plane-tree leaves on the sea breeze (the city's own weather, 3 depths) ================= */
const LEAF_COLS = [
  { base: '#b8a040', dark: '#6a5a20', light: '#e0c868', back: '#8a7a30', vein: '#c8b058' },
  { base: '#d89a34', dark: '#8a5a1a', light: '#f6c060', back: '#b07a2a', vein: '#e8b050' },
  { base: '#9a6a32', dark: '#5a3a1a', light: '#c8905a', back: '#7a5228', vein: '#b08048' },
  { base: '#8aa040', dark: '#4a5a20', light: '#b8c868', back: '#6a7a30', vein: '#a0b050' },
];
const LEAF_SHAPES = [['11', '10'], ['10101', '11111', '01110', '00100'],
  ['000010000', '010010010', '011010110', '111111111', '011111110', '001111100', '000111000', '000010000']];
//                 count nMin nMax amp drift  land
const LEAF_DEPTHS = [[18, 6, 9, 4, 34, [150, 170]], [14, 8, 11, 7, 52, [206, 234]], [7, 11, 14, 12, 76, [H + 12, H + 12]]];
const LEAVES = LEAF_DEPTHS.map(([count, nMin, nMax], li) => {
  const r = rng(8300 + li);
  return Array.from({ length: count }, (_, i) => ({ id: i + li * 1000, n: nMin + Math.floor(r() * (nMax - nMin + 1)), ph: r(),
    ks: 30 + Math.floor(r() * 30), p2: r(), kr: 20 + Math.floor(r() * 25), p3: r(), kf: 24 + Math.floor(r() * 40), p4: r(), amp: 0.7 + r() * 0.6, drift: 0.5 + r(), col: Math.floor(r() * 4) }));
});
function makeLeaf(lc, size, back) {
  const S = LEAF_SHAPES[size], w = S[0].length, h = S.length, mid = w >> 1, [c, x] = makeCanvas(w, h);
  S.forEach((row, j) => { for (let i = 0; i < w; i++) if (row[i] === '1') {
    let col = back ? lc.back : lc.base;
    if (size > 0 && i === mid && j > 0) col = back ? lc.vein : lc.dark;
    else if (!back && size > 0 && i < mid && j < h - 3 && (i + j) % 3 === 0) col = lc.light;
    else if (size === 2 && (j === h - 3 || i === 0 || i === w - 1)) col = lc.dark;
    x.fillStyle = col; x.fillRect(i, j, 1, 1);
  } });
  return c;
}
function drawLeaves(depth, t) {
  const [, , , amp, drift, band] = LEAF_DEPTHS[depth], S = LEAF_SHAPES[depth], w = S[0].length, h = S.length;
  for (const l of LEAVES[depth]) {
    const { cyc, u } = cycle(t, l.n, l.ph), sw = wave(t, l.ks, l.p2);
    const yEnd = lerp(band[0], band[1], hash(l.id, cyc, 6));
    const y = -12 + u * (yEnd + 12) - Math.abs(sw) * (depth + 1) * 1.5;
    const x = -40 + hash(l.id, cyc, 5) * (W + 10) + u * drift * l.drift + sw * amp * l.amp;
    if (x < -12 || x > W + 12) continue;
    const fl = Math.cos(TAU * (l.kf * t / LOOP + l.p4)), spr = LEAF_SPR[(l.col + cyc) % 4][depth][fl < 0 ? 1 : 0];
    ctx.globalAlpha = (band[0] < H && u > 0.88 ? (1 - u) / 0.12 : 1) * (depth === 0 ? 0.55 : 1);
    if (depth === 0) { ctx.drawImage(spr, Math.round(x), Math.round(y)); continue; }
    ctx.save();
    ctx.translate(Math.round(x), Math.round(y));
    ctx.rotate(sw * 0.9 + 0.3 * wave(t, l.kr, l.p3));
    ctx.scale(1, Math.max(0.25, Math.abs(fl)));
    ctx.drawImage(spr, -(w >> 1), -(h >> 1));
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}
// moths round the lamps and the bar's door
const MOTH_AT = [{ x: LAMPS[0].x, y: LAMPS[0].y }, { x: LAMPS[1].x, y: LAMPS[1].y }, { x: DOOR.x, y: 200 }];
const MOTHS = [];
MOTH_AT.forEach((m, j) => { const r = rng(90 + j); for (let i = 0; i < 4; i++) MOTHS.push({ ...m, rx: 3 + r() * 6, ry: 2 + r() * 5, k: 60 + Math.floor(r() * 50), k2: 23 + Math.floor(r() * 30), ph: r(), dir: r() < 0.5 ? 1 : -1 }); });
function drawMoths(t) {
  for (const m of MOTHS) {
    const an = TAU * (m.k * t / LOOP + m.ph) * m.dir, x = Math.round(m.x + Math.cos(an) * m.rx + wave(t, m.k2, m.ph) * 2), y = Math.round(m.y + Math.sin(an) * m.ry);
    R(x, y, 1, 1, hash(m.k, step(t, 2400), 3) < 0.5 ? '#fff4d8' : '#c8b898');
  }
}
// where drops and flakes of a picked weather catch the light: the plaza lamps, the terrace, the bar
const LIGHTS = [
  { x: LAMPS[0].x, y: LAMPS[0].y + 8, rx: 26, ry: 30, col: '#ffe2ac' }, { x: LAMPS[1].x, y: LAMPS[1].y + 8, rx: 26, ry: 30, col: '#ffe2ac' },
  { x: 400, y: 170, rx: 52, ry: 22, col: '#ffd898' }, { x: DOOR.x, y: 206, rx: 18, ry: 22, col: '#ffcf80' }, { x: JX, y: 110, rx: 70, ry: 40, col: '#ffe0b0' },
];
const LIGHTS_BB = [{ x: BILL.x + (BILL.w >> 1), y: BILL.y + 4, rx: 30, ry: 14, col: '#fff0cc' }, ...LIGHTS];
const WEATHER = weatherPlans(['leaves']);
const WX_LAND = [[150, 170], [206, 236], [H + 4, H + 4]];
const WX = { land: WX_LAND, lights: LIGHTS }, WX_BB = { land: WX_LAND, lights: LIGHTS_BB };

/* ================= Who is there, and their ranks ================= */
const AUD = [   // the guitarist's ring: x, feet, look, view, dir, rank (a parent and child share one)
  { x: 108, y: 228, who: 'aud1', view: 0, rank: spread(1) }, { x: 152, y: 224, who: 'aud2', view: 2, dir: -1, rank: CAST, film: true },
  { x: 94, y: 222, who: 'aud3', view: 2, dir: 1, rank: spread(0), kid: true }, { x: 140, y: 230, who: 'aud4', view: 0, rank: spread(2) },
];
const STROLL_RANK = spread(3), TOUR_RANK = spread(4), PALS_RANK = spread(5), JOG_RANK = spread(6), T3_RANK = spread(7), OLD_RANK = spread(8);
const WALKERS = [   // people walking through: look, feet row, [from, to, direction] windows; `mate` walks beside, chatting
  { who: 'tour', y: 227, pass: [[6, 38, 1], [148, 180, -1]], rank: TOUR_RANK, phone: true },
  { who: 'pal1', mate: 'pal2', y: 231, pass: [[78, 112, -1], [200, 234, 1]], rank: PALS_RANK },
  { who: 'armA', mate: 'armB', y: 229, pass: [[STROLL.t0, STROLL.t1, 1]], rank: STROLL_RANK, arm: true },
  { who: 'jog', y: 232, pass: [[JOG.t0, JOG.t1, -1]], rank: JOG_RANK, run: true },
];

/* ================= The people, per frame (drawn in order of their feet's row) ================= */
const GLASS = ['#a83a2a', '#f0b830', '#f4e8b0'];
const slotX = i => DOCK.x + i * DOCK.gap;
const docked = t => !(t >= TAKE.wheel[0] && t < BRING.beep);                 // TAKE_SLOT's bike is in its dock
const walkX = (a, b, dir, t, pad = 24) => Math.round(dir > 0 ? lerp(-pad, W + pad, (t - a) / (b - a)) : lerp(W + pad, -pad, (t - a) / (b - a)));
function drawRider(x, P, dir, t, bike, rank) {          // a rider on a bike along the lane
  const fr = Math.floor(t * 3.2) & 1;
  ctx.drawImage(bike[dir > 0 ? 0 : 1], x - 9, LANE - 10);
  person(x - dir, LANE - 8 + P.ll - 1, P, { view: 2, dir, pedal: fr, aR: 3 });
}
function drawTable(T, k, t, crowd, waiterAt) {
  const guests = [[T.x - 6, 1, k === 0 ? 'g1' : k === 1 ? 'g3' : 'g5'], [T.x + 7, -1, k === 0 ? 'g2' : k === 1 ? 'g4' : 'g6']];
  const taken = k < 2 ? seen(CAST, crowd) : seen(T3_RANK, crowd);
  for (const [gx, d, who] of guests) {                                        // the chairs
    R(gx - d * 2, T.y - 10, 1, 10, '#3a3a44'); R(gx - 2, T.y - 5, 5, 1, '#5a5a66'); R(gx + 2 * d, T.y - 4, 1, 4, '#2a2a34');
    if (!taken) continue;
    const s = step(t + k * 3.3 + d, 320), drink = hash(k * 5 + d, s, 61) < 0.18, talk = hash(k * 5 + d, s, 62) < 0.4;
    person(gx, T.y, WHO[who], { view: 2, dir: d, sit: true, aR: drink ? 6 : talk ? 3 : 4, lean: hash(k, step(t, 160), 63) < 0.2 ? d : 0 });
  }
  R(T.x - 1, T.y - 7, 2, 7, '#2a2a34'); R(T.x - 3, T.y, 6, 1, '#2a2a34');    // the pedestal, the foot
  R(T.x - 4, T.y - 8, 9, 1, '#d8d0c4'); R(T.x - 4, T.y - 7, 9, 1, '#8a8078');   // the marble top
  if (taken) { R(T.x - 2, T.y - 10, 1, 2, GLASS[k]); R(T.x + 2, T.y - 10, 1, 2, GLASS[(k + 1) % 3]); if (waiterAt === k) R(T.x, T.y - 10, 1, 2, GLASS[(k + 2) % 3]); }
  R(T.x, T.y - 9, 1, 1, '#ffd890'); halo(HL.candle, T.x, T.y - 9, 0.7 + 0.3 * wave(t, 90 + k * 13, k * 0.3));
}
function drawPeople(t, crowd) {
  const DL = [], put = (y, fn) => DL.push([y, fn]), fr = Math.floor(t * 3.2) & 1;
  const cast = seen(CAST, crowd);
  // ---- the couple on the bench, looking at the basilica; she takes a picture now and then, he points out the cranes
  put(BENCH.y + 0.5, () => {
    if (cast) {
      const photo = frac((t - 30) / 48) < 0.14, point = frac((t - 20) / 60) < 0.08;
      person(BENCH.x + 9, BENCH.y, WHO.he, { sit: true, aL: point ? 1 : 0, aR: photo ? 0 : 9 });
      person(BENCH.x + 17, BENCH.y, WHO.she, { sit: true, lean: photo ? 0 : -1, aR: photo ? 2 : 0 });
      if (photo) halo(HL.screen, BENCH.x + 21, BENCH.y - 20, 0.6);
    }
    for (let x = BENCH.x; x < BENCH.x + 27; x++) {                         // the bench's back, slats and ends
      R(x, BENCH.y - 7, 1, 1, '#7a5636'); R(x, BENCH.y - 6, 1, 1, '#3a2818'); R(x, BENCH.y - 4, 1, 1, '#5a3e28'); R(x, BENCH.y - 3, 1, 1, '#2a1c14');
    }
    for (const ex of [BENCH.x, BENCH.x + 26]) R(ex, BENCH.y - 8, 1, 9, '#1a1a20');
  });
  // ---- the guitarist under the lamp, his case open at his feet, and his ring of listeners
  const song = ((t - SONG_END) % SONG + SONG) % SONG, clap = song < 3;
  if (cast) put(218, () => {
    const G = WHO.guitar, x = 126, ty = 218 - G.ll - G.th + 1;
    R(x + 2, 219, 12, 3, '#1a1418'); R(x + 3, 219, 10, 2, '#8a2a2a');           // the case, its red lining
    for (const cx of [x + 5, x + 9, x + 11]) R(cx, 220, 1, 1, hash(cx, step(t, 240), 3) < 0.3 ? '#fff4c8' : '#c8a048');
    person(x, 218, G, { view: 1, aL: 3, aR: clap ? 0 : fr ? 4 : 8, lean: hash(1, step(t, 120), 9) < 0.5 ? 0 : 1 });
    R(x - 3, ty + 5, 6, 4, '#a86a32'); R(x - 2, ty + 6, 4, 2, '#c8884a'); R(x - 1, ty + 6, 2, 2, '#1a1010');   // the guitar
    for (let k = 1; k <= 7; k++) R(x - 3 - k, ty + 6 - Math.round(k * 0.5), 1, 1, '#3a2418');
  });
  for (const [i, A] of AUD.entries()) {
    if (!seen(A.rank, crowd)) continue;
    const P = WHO[A.who];
    put(A.y, () => {
      const sway = hash(i, step(t, 120), 12) < 0.5 ? 0 : (i & 1 ? 1 : -1);
      if (A.film) person(A.x, A.y, P, { view: 2, dir: A.dir, aR: clap ? (fr ? 4 : 6) : 2 });
      else if (A.view === 0) person(A.x, A.y, P, { view: 0, lean: sway, aL: clap ? (fr ? 7 : 8) : 0, aR: clap ? (fr ? 7 : 8) : 0 });
      else person(A.x, A.y, P, { view: 2, dir: A.dir, aR: clap ? (fr ? 4 : 3) : 0 });
      if (A.kid) {                                                           // the child runs a coin over to the case
        const q = song - 0.2, out = q >= 0 && q < 3.6, u = out ? Math.sin(Math.PI * q / 3.6) : 0;
        const kx = Math.round(lerp(A.x + 7, 136, u)), ky = Math.round(lerp(A.y + 1, 223, u));
        person(kx, ky, WHO.kid, { view: 2, dir: q < 1.8 ? 1 : -1, step: out ? fr : 0, aR: out && q > 1.4 && q < 2.2 ? 3 : 0 });
      }
    });
  }
  // ---- people walking through
  for (const Wk of WALKERS) {
    if (!seen(Wk.rank, crowd)) continue;
    for (const [a, b, dir] of Wk.pass) {
      if (t < a || t >= b) continue;
      const x = walkX(a, b, dir, t), P = WHO[Wk.who], f = Math.floor(t * (Wk.run ? 5.6 : 3.2)) & 1;
      put(Wk.y, () => {
        if (Wk.mate) person(x - dir * 6, Wk.y - 1, WHO[Wk.mate], { view: 2, dir, step: !f, aR: Wk.arm ? 9 : hash(3, step(t, 240), 5) < 0.3 ? 3 : 0, lean: Wk.arm ? dir : 0 });
        person(x, Wk.y, P, { view: 2, dir, step: f, aR: Wk.phone ? (frac(t / 16) < 0.4 ? 2 : 0) : Wk.run ? (f ? 4 : 0) : 0 });
      });
    }
  }
  // ---- someone walks a dog (they share a rank: both are the cast of the cat's moment)
  for (const [a, b, dir] of DOGS.map(d => [d.t0, d.t1, d.dir])) {
    if (!cast || t < a || t >= b) continue;
    const x = walkX(a, b, dir, t), P = WHO.dogw, y = 230;
    put(y, () => {
      person(x, y, P, { view: 2, dir, step: fr, aR: 3 });
      const dx = dir > 0 ? x + 9 : x - 17, hx = x + dir * 4, hy = y - P.ll - 4, nx = dir > 0 ? dx + 6 : dx + 1;
      ctx.drawImage(DOG_C[fr][dir > 0 ? 0 : 1], dx, y - 4);
      for (let k = 0; k <= 6; k++) R(Math.round(lerp(hx, nx, k / 6)), Math.round(lerp(hy, y - 3, k / 6) + Math.sin(k / 6 * Math.PI) * 2), 1, 1, '#5a4a40');
    });
  }
  // ---- the cat on the pond's rim: sits, washes, trots off when the dog comes by
  put(P1, () => {
    let x = CAT_X[0], moving = null;
    for (const [a, b, d] of CAT_MOVES) {
      if (t >= b) x = d > 0 ? CAT_X[1] : CAT_X[0];
      if (t >= a && t < b) { moving = d; x = Math.round(lerp(d > 0 ? CAT_X[0] : CAT_X[1], d > 0 ? CAT_X[1] : CAT_X[0], (t - a) / (b - a))); }
    }
    if (moving !== null) ctx.drawImage(CAT_C.walk[Math.floor(t * 4) & 1][moving > 0 ? 0 : 1], x - 4, P1 - 4);
    else { ctx.drawImage(CAT_C.sit, x - 2, P1 - 5); if (frac(t / 20) < 0.2) R(x + 3, P1 - 3 - (Math.floor(t * 3) & 1), 1, 1, '#d8c8b0'); else R(x - 3, P1 - 2 + (wave(t, 30) > 0 ? 0 : 1), 1, 2, '#1a1618'); }
  });
  // ---- the bike dock, and the two riders who take a bike out and bring one back
  put(KERB - 1, () => {
    for (let i = 0; i < DOCK.n; i++) {
      const x = slotX(i), here = i !== TAKE_SLOT || docked(t) || !cast;
      if (here) ctx.drawImage(SHARE_C[0], x - 3, KERB - 10, 12, 11);
      R(x + 8, KERB - 8, 2, 8, '#3a3a44'); R(x + 8, KERB - 8, 2, 1, '#7a7a84');
      const beep = (Math.abs(t - TAKE.beep - 0.5) < 0.6 || Math.abs(t - BRING.beep - 0.5) < 0.6) && i === TAKE_SLOT && cast;
      R(x + 8, KERB - 7, 1, 1, beep ? (frac(t * 4) < 0.5 ? '#aaffc0' : '#3a8a5a') : here ? '#4ae07a' : '#e04a3a');
      if (beep) halo(HL.led, x + 8, KERB - 7, 0.7);
    }
  });
  if (cast) {
    const sx = slotX(TAKE_SLOT);
    if (t >= TAKE.walk[0] && t < TAKE.walk[1]) { const u = (t - TAKE.walk[0]) / (TAKE.walk[1] - TAKE.walk[0]), x = Math.round(lerp(W + 20, sx + 6, u)); put(230, () => person(x, 230, WHO.taker, { view: 2, dir: -1, step: fr })); }
    else if (t >= TAKE.walk[1] && t < TAKE.wheel[0]) put(234, () => person(sx + 1, 234, WHO.taker, { view: 0, aL: 4, aR: t > TAKE.beep - 1 ? 3 : 4 }));
    else if (t >= TAKE.wheel[0] && t < TAKE.wheel[1]) {
      const u = (t - TAKE.wheel[0]) / (TAKE.wheel[1] - TAKE.wheel[0]), x = Math.round(lerp(sx, sx - 18, u)), y = Math.round(lerp(KERB + 1, LANE, u));
      put(y, () => { ctx.drawImage(SHARE_C[1], x - 9, y - 10); person(x + 6, y, WHO.taker, { view: 2, dir: -1, step: fr, aR: 3 }); });
    } else if (t >= TAKE.ride[0] && t < TAKE.ride[1]) { const u = (t - TAKE.ride[0]) / (TAKE.ride[1] - TAKE.ride[0]), x = Math.round(lerp(sx - 18, -30, u)); put(LANE, () => drawRider(x, WHO.taker, -1, t, SHARE_C)); }
    if (t >= BRING.ride[0] && t < BRING.ride[1]) { const u = (t - BRING.ride[0]) / (BRING.ride[1] - BRING.ride[0]), x = Math.round(lerp(-30, sx - 4, u)); put(LANE, () => drawRider(x, WHO.bringer, 1, t, SHARE_C)); }
    else if (t >= BRING.ride[1] && t < BRING.beep) {
      const u = (t - BRING.ride[1]) / (BRING.beep - BRING.ride[1]), x = Math.round(lerp(sx - 4, sx, u)), y = Math.round(lerp(LANE, KERB + 1, u));
      put(y, () => { ctx.drawImage(SHARE_C[0], x - 9, y - 10); person(x - 6, y, WHO.bringer, { view: 2, dir: 1, step: fr, aR: 3 }); });
    } else if (t >= BRING.beep && t < BRING.walk[0]) put(234, () => person(sx + 1, 234, WHO.bringer, { view: 0, aR: 4 }));
    else if (t >= BRING.walk[0] && t < BRING.walk[1]) { const x = walkX(BRING.walk[0], BRING.walk[1], 1, t); if (x > sx) put(230, () => person(x, 230, WHO.bringer, { view: 2, dir: 1, step: fr })); }
    for (const Rd of RIDES) if (t >= Rd.t0 && t < Rd.t1) { const x = walkX(Rd.t0, Rd.t1, Rd.dir, t, 30); put(LANE, () => drawRider(x, WHO[Rd.who], Rd.dir, t, BIKE_C)); }
  }
  // ---- the terrace: three tables, the waiter's round
  const q = ((t - 2) % SERVE + SERVE) % SERVE, round = Math.floor(((t - 2) % LOOP + LOOP) % LOOP / SERVE), tk = round % 2;
  const stand = [[372, 215], [404, 222]][tk], served = cast && q >= 7.5 && q < 30 ? tk : -1;
  TABLES.forEach((T, k) => put(T.y, () => drawTable(T, k, t, crowd, served)));
  if (cast) {
    let wx = DOOR.x - 4, wy = DOOR.y, view = 1, dir = -1, aR = fr && q > 20 && q < 24 ? 4 : 0, tray = 0;
    if (q < 6) { const u = q / 6; wx = Math.round(lerp(DOOR.x, stand[0] + 6, u)); wy = Math.round(lerp(DOOR.y, stand[1], u)); view = 2; dir = -1; aR = 5; tray = 3; }
    else if (q < 9) { wx = stand[0] + 6; wy = stand[1]; view = 1; aR = 3; tray = q < 7.5 ? 3 : 2; }
    else if (q < 15) { const u = (q - 9) / 6; wx = Math.round(lerp(stand[0] + 6, DOOR.x, u)); wy = Math.round(lerp(stand[1], DOOR.y, u)); view = 2; dir = 1; aR = 4; tray = -1; }
    put(wy, () => {
      const P = WHO.waiter, hy = person(wx, wy, P, { view, dir, step: view === 2 ? fr : 0, aR }), ty = hy + P.hh;
      if (tray > 0) {
        const tx = view === 2 ? wx + dir * 2 : wx + 7, yy = view === 2 ? ty - 2 : ty + 1;
        R(tx - 3, yy, 7, 1, '#b8b8c4');
        for (let g = 0; g < tray; g++) R(tx - 2 + g * 2, yy - 2, 1, 2, GLASS[g]);
      } else if (tray < 0) R(wx + dir * 2, ty + 3, 1, 4, '#9a9aa6');
    });
  }
  put(KERB + 0.2, () => { blit(TRUNKS[0]); blit(TRUNKS[1]); });              // the plane trees on the kerb
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, fn] of DL) fn();
}

/* ================= Carrer de Lepant: the taxis and the mopeds ================= */
function drawLepant(t) {
  for (const Tx of TAXIS) {
    if (t < Tx.t0 || t >= Tx.t1) continue;
    const u = (t - Tx.t0) / (Tx.t1 - Tx.t0), x = Math.round(Tx.dir > 0 ? lerp(-66, W + 6, u) : lerp(W + 6, -66, u)), y = ROAD - 20;
    const front = Tx.dir > 0 ? x + 60 : x;
    ctx.globalAlpha = 0.35; R(x + 4, ROAD, 52, 1, '#05050a'); ctx.globalAlpha = 1;
    for (let k = 0; k < 4; k++) { ctx.globalAlpha = 0.1 - k * 0.022; R(Tx.dir > 0 ? front + k * 14 : front - (k + 1) * 14, ROAD - 6, 14, 6, '#fff2d0'); }   // its lamps on the asphalt ahead
    ctx.globalAlpha = 1;
    ctx.drawImage(TAXI_C[Tx.free ? 0 : 1][Tx.dir > 0 ? 0 : 1], x, y);
    for (const wx of [11, 48]) { const an = -x / 4.3 * Tx.dir, cx = x + (Tx.dir > 0 ? wx : 59 - wx); R(Math.round(cx + Math.cos(an) * 2), Math.round(y + 16 + Math.sin(an) * 2), 1, 1, '#c8c8d4'); }
    halo(HL.head, front, y + 13, 1); halo(HL.tail, Tx.dir > 0 ? x : x + 60, y + 13, 0.8);
    halo(Tx.free ? HL.free : HL.tail, x + 30, y + 4, 0.6);
  }
  for (const M of MOPEDS) {
    if (t < M.t0 || t >= M.t1) continue;
    const u = (t - M.t0) / (M.t1 - M.t0), x = Math.round(M.dir > 0 ? lerp(-30, W + 4, u) : lerp(W + 4, -30, u)), y = ROAD - 21;
    ctx.globalAlpha = 0.35; R(x + 3, ROAD, 20, 1, '#05050a'); ctx.globalAlpha = 1;
    ctx.drawImage(MOPED_C[M.dir > 0 ? 0 : 1], x, y);
    halo(HL.head, M.dir > 0 ? x + 22 : x + 3, y + 10, 0.8); halo(HL.tail, M.dir > 0 ? x + 2 : x + 23, y + 13, 0.7);
  }
}

/* ================= Gulls crossing over the towers ================= */
const FLAP = [['1...1', '.1.1.', '..2..'], ['.....', '11.11', '..2..'], ['.....', '.121.', '1...1']];
function drawGulls(t) {
  for (const [a, b, dir] of GULL_T) {
    if (t < a || t >= b) continue;
    const u = (t - a) / (b - a);
    for (let g = 0; g < 3; g++) {
      const x = Math.round(dir > 0 ? lerp(-20 - g * 14, W + 10, u) : lerp(W + 20 + g * 14, -10, u)), y = Math.round(38 + g * 7 + 6 * Math.sin(u * 5 + g) + 3 * wave(t, 40, g * 0.3));
      const litNow = Math.abs(x - JX) < 70 ? 1 : 0, fr = hash(g, step(t, 240), 4) < 0.4 ? 1 : [0, 1, 2, 1][(Math.floor(t * 5) + g) % 4];
      ctx.drawImage(GULL_SPR[litNow][fr], x - 2, y - 1);
    }
  }
}

/* ================= The near blocks, per frame ================= */
const LWINS = [{ id: 900, n: 5, p: 0.7, off: 0.1 }, { id: 901, n: 4, p: 0.6, off: 0.6 }, { id: 902, n: 6, p: 0.5, off: 0.3 }, { id: 903, n: 5, p: 0.6, off: 0.8 }];
const SENYERA = ['#f2c230', '#c8302a'];
function drawDoor(x, fy, col, col2) { R(x, fy - 29, 6, 29, col); R(x + 7, fy - 29, 6, 29, col); R(x, fy - 29, 13, 8, col2); }
function drawLeftBlock(t, crowd) {
  ctx.drawImage(LEFT_C, 0, 0);
  const [F0, F1, F2, F3] = LFLOORS;
  // the top floor: a lit room, the old man at the rail with his cigarette
  if (lit(LWINS[0], t)) drawDoor(12, F0, '#c8843a', '#f0b860');
  if (seen(OLD_RANK, crowd)) {
    const drag = frac(t / 12) < 0.18;
    person(30, F0 - 1, WHO.old, { view: 1, sit: true, aL: drag ? 6 : 4 });
    const ex = drag ? 30 : 27, ey = drag ? F0 - 15 : F0 - 11;
    R(ex, ey, 1, 1, drag ? '#ffb060' : '#c8582a'); halo(HL.ember, ex, ey, drag ? 0.9 : 0.4);
    for (let k = 0; k < 5; k++) { const u = frac(t / 6 + k / 5); ctx.globalAlpha = 0.3 * (1 - u); R(ex + Math.round(Math.sin(u * 6 + k) * 1.5 + u * 3), ey - 1 - Math.round(u * 12), 1, 1, '#b8b0c0'); }
    ctx.globalAlpha = 1;
  }
  // the next floor down: the television's light through the open door, ivy hanging off the rail, the senyera
  { const s = step(t, 720), k = hash(5, s, 2);
    drawDoor(12, F1, k < 0.3 ? '#252e5c' : k < 0.7 ? '#33427e' : '#46589a', k < 0.5 ? '#3e5090' : '#5a6eb0'); halo(HL.tv, 18, F1 - 14, 0.5 + 0.4 * k); }
  for (let i = 0; i < 9; i++) {
    const len = 10 + (i % 3) * 2;
    for (let j = 0; j < len; j++) {
      const off = Math.round(Math.sin(TAU * 24 * t / LOOP - j * 0.45 + i * 0.3) * (j / len) * 1.6);
      R(3 + i + off, F1 - 8 + j, 1, 1, j === len - 1 ? '#8a2420' : SENYERA[i & 1]);
    }
  }
  for (let i = 0; i < 4; i++) for (let j = 0; j < 7 + i * 2; j++) R(27 + i * 2 + Math.round(wave(t, 18, i * 0.2 + j * 0.05) * j / 8), F1 + 1 + j, 1, 1, (j + i) % 3 ? '#2a4a26' : '#3e6a32');
  // the neighbour's floor: her room lights up, she comes out and waters the geraniums, water drips from the pots
  const w = WATERING.find(([a, b]) => t >= a - 4 && t < b + 6), out = WATERING.find(([a, b]) => t >= a && t < b);
  drawDoor(12, F2, w ? '#d8904a' : '#2a1a14', w ? '#f6c070' : '#3a2418');
  if (w) halo(HL.room, 18, F2 - 14, 0.8);
  for (const [px0, flo] of [[3, '#d83a3a'], [8, '#e85a6a'], [29, '#d83a3a'], [33, '#f08a3a']]) {
    const sw = Math.round(wave(t, 22, px0 * 0.07) * 0.8);
    R(px0, F2 - 12, 4, 3, '#8a4a2a'); R(px0 + sw, F2 - 15, 4, 3, '#2e5a2a'); R(px0 + 1 + sw, F2 - 16, 1, 1, flo); R(px0 + 3 + sw, F2 - 15, 1, 1, flo); R(px0 + sw, F2 - 14, 1, 1, flo);
  }
  if (out && seen(CAST, crowd)) {
    const [a, b] = out, q = t - a, inn = q < 2 ? q / 2 : b - t < 2 ? (b - t) / 2 : 1, x = Math.round(lerp(18, 24, inn)), watering = q > 3 && b - t > 3;
    ctx.globalAlpha = clamp(inn * 1.5, 0, 1);
    const hy = person(x, F2 - 1, WHO.nbr, { view: 1, aR: watering ? 3 : 4, aL: 0 }), ty = hy + WHO.nbr.hh;
    if (watering) { R(x + 6, ty, 4, 3, '#3a8a5a'); R(x + 10, ty + 1, 2, 1, '#3a8a5a'); for (let k = 0; k < 3; k++) if (hash(k, step(t, 1440), 7) < 0.7) R(x + 12, ty + 2 + k, 1, 1, '#b8d8ff'); }
    ctx.globalAlpha = 1;
  }
  for (const [a, b] of WATERING) if (t >= a + 6 && t < b + 1) for (let k = 0; k < 4; k++) {   // drips off the slab to the pavement
    const { u } = cycle(t, 160, k / 4), y = Math.round(F2 + 3 + u * u * (262 - F2 - 3));
    R(31 + (k & 1) * 3, y, 1, 2, '#9ab8e0');
  }
  // the floor above the shop: shutters down, light through the slats, someone moving in the room
  for (let y = F3 - 29; y < F3; y++) {
    const shadow = Math.abs(((frac(t / 20) * 30) | 0) - (12 + (y & 7))) < 4;
    R(12, y, 13, 1, (y & 1) ? '#24382c' : lit(LWINS[2], t) && !shadow ? '#d8a050' : '#3a2a1c');
  }
  // the pharmacy's green cross over the shop, its light sweeping slowly down
  const band = Math.floor(frac(t / 4) * 12);
  for (let j = 0; j < 9; j++) for (let i = 0; i < 9; i++) {
    if (!((i >= 3 && i < 6) || (j >= 3 && j < 6))) continue;
    R(35 + i, 186 + j, 1, 1, Math.abs(j - band) < 2 ? '#aaffb0' : (i + j) & 1 ? '#2ac85a' : '#1a9a44');
  }
  halo(HL.pharm, 39, 190, 0.75 + 0.15 * wave(t, 60));
  halo(HL.door, 36, 214, 0.6);
}
function drawRightBlock(t, crowd) {
  ctx.drawImage(RIGHT_C, 0, 0);
  RFLOORS.forEach((fy, i) => { if (lit(LWINS[(i + 1) % 4], t, 7)) { R(458, fy - 29, 7, 29, i === 1 ? '#e0a050' : '#c8843a'); R(466, fy - 29, 6, 29, '#b87838'); } });
  if (seen(CAST, crowd)) {                                                   // the barman behind the counter
    const x = 470 + Math.round(3 * wave(t, 12)), polish = frac(t / 8) < 0.4;
    R(x - 2, 206, 5, 15, '#ece4d8'); R(x - 1, 202, 3, 4, '#c08858'); R(x - 1, 201, 3, 1, '#1a1210');
    R(polish ? x - 4 : x + 3, polish ? 210 + (Math.floor(t * 4) & 1) : 212, 2, 2, '#c08858');
  }
  halo(HL.door, DOOR.x, 210, 0.85 + 0.1 * wave(t, 50)); halo(HL.sign, RBLD + 22, 178, 0.6);
}
function drawCanopies(t) {
  CANOPY_C.forEach((layers, ti) => layers.forEach((L, k) => {
    const dx = Math.round(wave(t, 14 + k * 5 + ti * 3, k * 0.27 + ti * 0.4) * 1.2), dy = Math.round(wave(t, 9 + k * 4, k * 0.4) * 0.6);
    ctx.drawImage(L.c, L.x + dx, L.y + dy);
  }));
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, FAR_C, FAR_BARE, SCAF_C, AD_C, PITCH, MASTS_C, BAS, PLAZA_C, WATER_C, GROUND_C, LEFT_C, RIGHT_C,
  TRUNKS, CANOPY_C, DUCK_C, SHARE_C, BIKE_C, TAXI_C, MOPED_C, DOG_C, CAT_C, GULL_SPR, LEAF_SPR, VIG_C;
function renderVignette() {
  const [c, x] = makeCanvas(W, H), img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 6; d[o + 1] = 6; d[o + 2] = 16; d[o + 3] = Math.round(q * 0.4 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}
function prerender() {
  HL = {
    moon: makeHalo(40, '#d8c8b0', 0.18, 1.8), moonCore: makeHalo(12, '#fff0d8', 0.3, 1.5), strobe: makeHalo(6, '#ffffff', 0.6, 1.2),
    bill: makeHalo(34, '#ffe0a0', 0.14, 1.6), billLamp: makeHalo(6, '#fff0c0', 0.5, 1.3), red: makeHalo(5, '#ff3a2a', 0.55, 1.2),
    floodW: makeHalo(62, '#ffb050', 0.14, 1.5), floodC: makeHalo(48, '#c8d8ff', 0.12, 1.5), cross: makeHalo(16, '#f0f6ff', 0.5, 1.4), evang: makeHalo(8, '#f4ecdc', 0.3, 1.4),
    star: makeHalo(12, '#d8e8ff', 0.55, 1.3), bell: makeHalo(14, '#ffe2a0', 0.4, 1.4), car: makeHalo(4, '#fff0d0', 0.4, 1),
    street: makeHalo(12, '#ffd890', 0.35, 1.4), screen: makeHalo(4, '#a0d8ff', 0.45, 1), lamp: makeHalo(44, '#ffc070', 0.22, 1.7),
    lampCore: makeHalo(10, '#fff0c8', 0.5, 1.4), bulb: makeHalo(4, '#ffd890', 0.35, 1.2), candle: makeHalo(5, '#ffb060', 0.4, 1.2),
    led: makeHalo(4, '#6aff8a', 0.5, 1.2), head: makeHalo(14, '#f4f8ff', 0.45, 1.3), tail: makeHalo(5, '#ff4030', 0.5, 1),
    free: makeHalo(5, '#4aff8a', 0.5, 1), door: makeHalo(28, '#ffb060', 0.22, 1.6), sign: makeHalo(18, '#f2b134', 0.2, 1.5),
    pharm: makeHalo(14, '#3aff7a', 0.3, 1.4), ember: makeHalo(3, '#ff8a3a', 0.6, 1), tv: makeHalo(14, '#7a9aff', 0.25, 1.4), room: makeHalo(16, '#ffb860', 0.3, 1.5),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  FAR_C = renderFar(true); WINS.length = 0; FAR_BARE = renderFar(false);
  SCAF_C = renderScaffold(); AD_C = renderAd(); PITCH = makePitch(BILL.w, BILL.h, { accent: '#f2b134' });
  MASTS_C = renderMasts(); BAS = renderBasilica();
  PLAZA_C = renderPlaza(); WATER_C = renderWater(); GROUND_C = renderGround();
  LEFT_C = renderLeftBlock(); RIGHT_C = renderRightBlock();
  TRUNKS = renderTrees().map(c => trim(c)); CANOPY_C = CANOPY.map(C => [0, 1, 2].map(k => renderCanopy(C, k)));
  DUCK_C = [{ H: '#2a5a3a', B: '#6a5a48', T: '#4a3e34' }, { H: '#5a4a3a', B: '#7a6a58', T: '#5a4e44' }].map(pal => {
    const d = sprite(DUCK.map(r => r.replace('B', 'B')), { ...pal, B: pal.B });
    return [flipH(d), d];
  });
  const share = renderShareBike(); SHARE_C = [share, flipH(share)];
  const bike = renderShareBike(); { const x = bike.getContext('2d'), img = x.getImageData(0, 0, 18, 11), d = img.data;   // a private bike: dark blue frame, no basket
    for (let i = 0; i < d.length; i += 4) if (d[i + 3] && d[i] > 180 && d[i + 1] > 180) { d[i] = 40; d[i + 1] = 70; d[i + 2] = 130; } else if (d[i + 3] && (d[i] === 106 || d[i] === 200)) d[i + 3] = 0;
    x.putImageData(img, 0, 0); }
  BIKE_C = [bike, flipH(bike)];
  TAXI_C = [true, false].map(f => { const c = renderTaxi(f); return [c, flipH(c)]; });
  const mp = renderMoped(); MOPED_C = [mp, flipH(mp)];
  DOG_C = [0, 1].map(f => [sprite(DOGS_SPR[f], DOG_COL, false), sprite(DOGS_SPR[f], DOG_COL, true)]);
  CAT_C = { sit: sprite(CAT_SIT, CAT_COL), walk: CAT_WALK.map(rows => [sprite(rows, CAT_COL), sprite(rows, CAT_COL, true)]) };
  GULL_SPR = [{ 1: '#9aa2be', 2: '#c6ccde' }, { 1: '#ffe2a0', 2: '#fff6d8' }].map(pal => FLAP.map(rows => sprite(rows, pal)));
  LEAF_SPR = LEAF_COLS.map(lc => [0, 1, 2].map(s => [makeLeaf(lc, s, false), makeLeaf(lc, s, true)]));
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
// pitch: a face over the canvas (promo captures, the website); billboard: false leaves the scaffold and its canvas out (the
// loop videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own leaves when left out; crowd: the share
// of the people to show, 0..1 (CROWD when left out or not a number), 0 = nobody
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  drawSky(t);
  drawGulls(t);
  if (wx.leaves) drawLeaves(0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  ctx.drawImage(billboard ? FAR_C : FAR_BARE, 0, 0);
  for (const w of WINS) {
    if (billboard && w.x + w.w > BILL.x - 5 && w.x < BILL.x + BILL.w + 5 && w.y > BILL.y - 12 && w.y < BILL.y + BILL.h + 14) continue;
    if (w.tv) { if (lit(w, t)) R(w.x, w.y, w.w, w.h, hash(w.id, step(t, 960), 4) < 0.5 ? '#6a88d0' : '#8aa8f0'); }
    else if (lit(w, t)) { R(w.x, w.y, w.w, w.h, w.col); R(w.x, w.y, w.w, 2, '#ffe2a8'); }
  }
  if (billboard) drawBillboard(t, pitch);
  drawCranes(t);
  drawBasilica(t);
  drawSwifts(t);
  ctx.drawImage(PLAZA_C, 0, 0);
  drawMarina(t);
  drawPalms(t);
  drawPond(t);
  ctx.drawImage(GROUND_C, 0, 0);
  LAMPS.forEach((l, i) => { const a = 0.94 + 0.06 * wave(t, 600 + i * 41, i * 0.3); halo(HL.lamp, l.x + 1, l.y + 10, a); halo(HL.lampCore, l.x + 1, l.y + 1, a); });
  if (wx.leaves) drawLeaves(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawRightBlock(t, crowd);
  drawPeople(t, crowd);
  drawLepant(t);
  drawCanopies(t);
  drawFestoon(t);
  drawMoths(t);
  drawLeftBlock(t, crowd);
  if (wx.leaves) drawLeaves(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'barcelona',
  name: 'Barcelona',
  tagline: 'Sea breeze by the Sagrada',
  timeZone: 'Europe/Madrid',
  accent: '#f2b134',                                   // trencadis ochre, written out: tools/seo/seo.mjs reads it from the source
  focusX: JX,
  landmark: [192, 314],                                // the Nativity's towers, the central towers and the Tower of Mary
  landmarkTop: CROSS_TOP - 1,                          // the top of the cross on the Tower of Jesus Christ
  billboard: BILL,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { crowd: 0.15, traffic: 0.15, crickets: 0.35, wind: 0.12, water: 0.18 },
  events: [
    { t: 30, type: 'gull', x: 220, gain: 0.3 },
    { t: SPLASH[0], type: 'splash', x: 198, gain: 0.25 },
    { t: CHIME_T, type: 'chime', x: JX, gain: 0.3 },
    ...BELLS.map(t => ({ t, type: 'bell', x: JX, gain: 0.5 })),
    { t: 168, type: 'gull', x: 300, gain: 0.25 },
    { t: SPLASH[1], type: 'splash', x: 300, gain: 0.25 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
